import { useState, useReducer, useEffect } from "react";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const COLORS = {
  navy: "#1a1a2e", blue: "#1a73e8", green: "#2e7d32", red: "#e53935",
  purple: "#7b1fa2", amber: "#f57c00", cyan: "#4fc3f7",
  navyLight: "#16213e", surface: "#ffffff", bg: "#f5f7fa",
  border: "#f0f0f0", text: "#1a1a2e", textMuted: "#888",
};

const bwp = (n) => {
  const num = parseFloat(n) || 0;
  const abs = Math.abs(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return (num < 0 ? "-BWP " : "BWP ") + abs;
};
const bwpParts = (n) => {
  const num = parseFloat(n) || 0;
  const abs = Math.abs(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return { sign: num < 0 ? "-" : "", amount: abs, negative: num < 0 };
};
const uid = () => Math.random().toString(36).slice(2, 10);
const today = () => new Date().toISOString().slice(0, 10);
const monthLabel = (m) => { if (!m) return ""; const [y, mo] = m.split("-"); return new Date(y, mo - 1, 1).toLocaleString("default", { month: "long", year: "numeric" }); };

// ─── INITIAL STATE ─────────────────────────────────────────────────────────────
const INIT = { setup: false, period: null, admin: null, ob: 0, obNote: "", adminHistory: [], members: [], contributions: [], expenses: [], archive: [], credits: [], expectedAmounts: [], transferLogs: [], pendingSettlements: [], excludedFromSettle: [], reminders: [], settings: { currency: "BWP", showOBOnDash: true, confirmDeletes: true, darkNav: true } };
// transferLogs:       [{ id, type:"member"|"pool", from, to, amount, date, clearedAt? }]  — visible in Contributions
// pendingSettlements: [{ key:"from|to", from, to, amount, fromPool, toPool }]             — unsettled, carries forward

// ─── SUPABASE CONFIG ──────────────────────────────────────────────────────────
const SUPA_URL = "https://cvgllfixfbtdzvhelqvh.supabase.co";
const SUPA_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImN2Z2xsZml4ZmJ0ZHp2aGVscXZoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzkzMTUzMzcsImV4cCI6MjA5NDg5MTMzN30.o8PG93q4Jm0MXas4j-uyPdK_7yngAR-CHgLohkh3IgY";
const SUPA_HEADERS = { "Content-Type": "application/json", "apikey": SUPA_KEY, "Authorization": "Bearer " + SUPA_KEY };

// Household ID — hardcoded so it NEVER changes even if browser data is cleared.
// All data in Supabase is stored under this ID. Do not change this value.
const HOUSEHOLD_ID = "housetab-main-household";

function getHouseholdId() {
  return HOUSEHOLD_ID;
}

async function supabaseLoad() {
  try {
    const id = getHouseholdId();
    const res = await fetch(`${SUPA_URL}/rest/v1/housetab_state?id=eq.${id}&select=state`, { headers: SUPA_HEADERS });
    if (!res.ok) return null;
    const rows = await res.json();
    return rows.length > 0 ? rows[0].state : null;
  } catch(e) { return null; }
}

async function supabaseSave(state) {
  try {
    const id = getHouseholdId();
    await fetch(`${SUPA_URL}/rest/v1/housetab_state`, {
      method: "POST",
      headers: { ...SUPA_HEADERS, "Prefer": "resolution=merge-duplicates" },
      body: JSON.stringify({ id, state, updated_at: new Date().toISOString() }),
    });
  } catch(e) { /* silent — localStorage still has it */ }
}

// ─── STATE PERSISTENCE ────────────────────────────────────────────────────────
function loadState() {
  // Load from localStorage instantly (no flicker)
  // Supabase sync happens in background after app mounts (see useEffect in App)
  try { const s = localStorage.getItem("housetab"); return s ? JSON.parse(s) : INIT; } catch(e) { return INIT; }
}

// ─── REDUCER ──────────────────────────────────────────────────────────────────
function reducer(state, action) {
  let next;
  switch (action.type) {
    case "SETUP": {
      const adminMember = { id: uid(), name: action.admin, active: true, createdAt: today() };
      const others = action.members.filter(n => n.trim()).map(n => ({ id: uid(), name: n.trim(), active: true, createdAt: today() }));
      next = { ...INIT, setup: true, period: { month: action.month, label: monthLabel(action.month) }, admin: adminMember.id, members: [adminMember, ...others] };
      break;
    }
    case "SET_OB": next = { ...state, ob: parseFloat(action.amount) || 0, obNote: action.note || "" }; break;
    case "SET_EXPECTED": {
      const existing = (state.expectedAmounts || []).filter(e => e.memberId !== action.memberId);
      next = { ...state, expectedAmounts: [...existing, { memberId: action.memberId, amount: parseFloat(action.amount) || 0, note: action.note || "", updatedAt: today() }] };
      break;
    }
    case "SETTLE_TXN": {
      // action: { key, from, to, amount, fromPool, toPool }
      // Never mutate ob — pool payouts are tracked in transferLogs and deducted in compute()
      const log = { id: uid(), from: action.from, to: action.to, amount: action.amount, fromPool: action.fromPool, toPool: action.toPool, date: today() };
      const newLogs = [...(state.transferLogs || []), log];
      const newPending = (state.pendingSettlements || []).filter(p => p.key !== action.key);
      next = { ...state, transferLogs: newLogs, pendingSettlements: newPending };
      break;
    }
    case "REVERSE_SETTLEMENT": {
      // action: { logId } — simply remove the log; pool recalculates naturally in compute()
      const newLogs = (state.transferLogs || []).filter(t => t.id !== action.logId);
      next = { ...state, transferLogs: newLogs };
      break;
    }
    case "PEND_SETTLEMENT": {
      // action: { key, from, to, amount, fromPool, toPool } — carry forward unsettled
      const alreadyPending = (state.pendingSettlements || []).find(p => p.key === action.key);
      if (alreadyPending) { next = state; break; }
      next = { ...state, pendingSettlements: [...(state.pendingSettlements || []), { key: action.key, from: action.from, to: action.to, amount: action.amount, fromPool: action.fromPool, toPool: action.toPool }] };
      break;
    }
    case "TOGGLE_SETTLE_EXCLUDE": {
      const current = state.excludedFromSettle || [];
      const next2 = current.includes(action.name)
        ? current.filter(n => n !== action.name)
        : [...current, action.name];
      next = { ...state, excludedFromSettle: next2 };
      break;
    }
    case "CLEAR_SETTLE_EXCLUDES":
      next = { ...state, excludedFromSettle: [] };
      break;
    case "CLEAR_SETTLEMENT": {
      // admin clears a pending carried-forward settlement
      next = { ...state, pendingSettlements: (state.pendingSettlements || []).filter(p => p.key !== action.key) };
      break;
    }
    case "SET_SETTINGS":
      next = { ...state, settings: { ...(state.settings || {}), ...action.settings } };
      break;
    case "ADD_REMINDER":
      next = { ...state, reminders: [...(state.reminders || []), action.reminder] };
      break;
    case "UPD_REMINDER":
      next = { ...state, reminders: (state.reminders || []).map(r => r.id === action.id ? { ...r, ...action.changes } : r) };
      break;
    case "DEL_REMINDER":
      next = { ...state, reminders: (state.reminders || []).filter(r => r.id !== action.id) };
      break;
    case "CLEAR_MONTH": {
      // Wipes current period data only, keeps members, OB, archive
      next = { ...state, contributions: [], expenses: [], transferLogs: [], pendingSettlements: [], expectedAmounts: [], excludedFromSettle: [] };
      break;
    }
    case "TOTAL_RESET": {
      next = { ...INIT };
      break;
    }
    case "RESTORE_BACKUP": {
      next = { ...action.data };
      break;
    }
    case "ADD_MEMBER": next = { ...state, members: [...state.members, action.member] }; break;
    case "TOGGLE_MEMBER": next = { ...state, members: state.members.map(m => m.id === action.id ? { ...m, active: !m.active } : m) }; break;
    case "ADD_CONTRIB": next = { ...state, contributions: [...state.contributions, action.contrib] }; break;
    case "UPD_CONTRIB": next = { ...state, contributions: state.contributions.map(c => c.id === action.id ? { ...c, status: action.status } : c) }; break;
    case "DEL_CONTRIB": next = { ...state, contributions: state.contributions.filter(c => c.id !== action.id) }; break;
    case "ADD_EXP": next = { ...state, expenses: [...state.expenses, action.exp] }; break;
    case "UPD_EXP": next = { ...state, expenses: state.expenses.map(e => e.id === action.id ? { ...e, ...action.changes } : e) }; break;
    case "MARK_PAID": next = { ...state, expenses: state.expenses.map(e => e.id === action.id ? { ...e, type: "actual", paidDate: action.date, paidBy: "pool" } : e) }; break;
    case "DEL_EXP": next = { ...state, expenses: state.expenses.filter(e => e.id !== action.id) }; break;
    case "TRANSFER": {
      const newAdminHistory = [...state.adminHistory, { memberId: action.to, date: today(), action: "transfer", keep: action.keep }];
      if (action.keep) {
        next = { ...state, admin: action.to, adminHistory: newAdminHistory };
      } else {
        next = { ...state, admin: action.to, adminHistory: newAdminHistory, contributions: [], expenses: [], ob: 0, obNote: "", credits: [] };
      }
      break;
    }
    case "CLOSE_ROLL": {
      next = { ...state, archive: [action.snap, ...state.archive], contributions: [], expenses: [], expectedAmounts: [], transferLogs: [], pendingSettlements: action.carryPending || [], excludedFromSettle: [], period: { month: action.month, label: monthLabel(action.month) }, ob: action.newOB, obNote: action.newOBNote, credits: action.credits, members: state.members.map(m => ({ ...m, active: action.active.includes(m.id) })) };
      break;
    }
    case "CLOSE_DIST": {
      next = { ...state, archive: [action.snap, ...state.archive], contributions: [], expenses: [], expectedAmounts: [], transferLogs: [], pendingSettlements: action.carryPending || [], excludedFromSettle: [], period: { month: action.month, label: monthLabel(action.month) }, ob: 0, obNote: "", credits: [], members: state.members.map(m => ({ ...m, active: action.active.includes(m.id) })) };
      break;
    }
    default: return state;
  }
  try { localStorage.setItem("housetab", JSON.stringify(next)); } catch(e) { }
  supabaseSave(next); // async, non-blocking
  return next;
}

// ─── COMPUTED ─────────────────────────────────────────────────────────────────
function compute(state) {
  const active = state.members.filter(m => m.active);
  const cashPaid = state.contributions.filter(c => c.status === "Paid").reduce((s, c) => s + c.amount, 0);
  const personalExps = state.expenses.filter(e => e.type === "actual" && e.paidBy !== "pool");
  const personalTotal = personalExps.reduce((s, e) => s + e.amount, 0);
  const combinedCash = cashPaid + personalTotal; // #2: personal merged into contributions total
  const actualExps = state.expenses.filter(e => e.type === "actual");
  const actualTotal = actualExps.reduce((s, e) => s + e.amount, 0);
  const plannedExps = state.expenses.filter(e => e.type === "planned");
  const plannedTotal = plannedExps.reduce((s, e) => s + e.amount, 0);
  const settledPoolPayouts = (state.transferLogs || []).filter(t => t.fromPool).reduce((s, t) => s + t.amount, 0);
  const grossPool = state.ob + cashPaid + personalTotal - actualTotal; // before any settlement payouts
  const pool = grossPool - settledPoolPayouts;
  const projected = pool - plannedTotal;
  // Default equal share (used when no expected amounts set)
  const sharePerMember = active.length ? actualTotal / active.length : 0;
  // OB credit only applies to members included in settlement
  const excludedFromSettle = new Set(state.excludedFromSettle || []);
  const settlingMembers = active.filter(m => !excludedFromSettle.has(m.name));
  const obCredit = (m) => excludedFromSettle.has(m.name) ? 0 : (settlingMembers.length > 0 ? state.ob / settlingMembers.length : 0);

  // #3: compute each member's expense share respecting appliesTo
  const memberExpenseShare = (memberId) => {
    return actualExps.reduce((total, exp) => {
      const applies = !exp.appliesTo || exp.appliesTo.length === 0
        ? active.map(m => m.id)
        : exp.appliesTo;
      if (!applies.includes(memberId)) return total;
      const splitCount = applies.filter(id => active.find(m => m.id === id)).length;
      return total + (splitCount > 0 ? exp.amount / splitCount : 0);
    }, 0);
  };

  const memberBalances = active.map(m => {
    const cash = state.contributions.filter(c => c.memberId === m.id && c.status === "Paid").reduce((s, c) => s + c.amount, 0);
    const personal = personalExps.filter(e => e.paidBy === m.id).reduce((s, e) => s + e.amount, 0);
    const credit = (state.credits || []).filter(c => c.memberId === m.id).reduce((s, c) => s + c.amount, 0);
    const memberObCredit = obCredit(m);
    const totalIn = cash + personal + credit + memberObCredit;
    const expectedEntry = (state.expectedAmounts || []).find(e => e.memberId === m.id);
    const expected = expectedEntry ? expectedEntry.amount : null;
    const expectedNote = expectedEntry ? expectedEntry.note : "";
    const expenseShare = memberExpenseShare(m.id);
    const share = expenseShare;
    const balance = totalIn - share;
    const cashShortfall = expected !== null ? expected - (cash + credit) : null;
    return { ...m, cash, personal, credit, obCredit: memberObCredit, totalIn, balance, share, expenseShare, expected, expectedNote, cashShortfall };
  });

  const totalExpected = (state.expectedAmounts || [])
    .filter(e => active.find(m => m.id === e.memberId))
    .reduce((s, e) => s + e.amount, 0);
  const totalExpectedSet = active.filter(m => (state.expectedAmounts || []).find(e => e.memberId === m.id)).length;

  const obCreditPerSettlingMember = settlingMembers.length > 0 ? state.ob / settlingMembers.length : 0;
  return { active, cashPaid, combinedCash, personalTotal, actualTotal, plannedTotal, pool, grossPool, projected, sharePerMember, obCredit: obCreditPerSettlingMember, memberBalances, totalExpected, totalExpectedSet };
}

// ─── ICONS ────────────────────────────────────────────────────────────────────
function Icon({ name, size = 22, color = "currentColor" }) {
  const paths = {
    home: "M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z",
    people: "M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z",
    wallet: "M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z",
    receipt: "M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z",
    balance: "M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3A4.49 4.49 0 0 0 8 20C19 20 22 3 22 3c-1 2-8 2-8 2H3L2 5h16C18 8 17.67 10 17 8zM16 11c.67 2 .33 4.67 0 6-3.93 0-6-3-6-3s4.67-1 6-3zM2 9l3.5 7h-7L2 9z",
    chart: "M5 9.2h3V19H5zM10.6 5h2.8v14h-2.8zm5.6 8H19v6h-2.8z",
    archive: "M20.54 5.23l-1.39-1.68C18.88 3.21 18.47 3 18 3H6c-.47 0-.88.21-1.16.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.48-.17-.93-.46-1.27zM12 17.5L6.5 12H10v-2h4v2h3.5L12 17.5zM5.12 5l.81-1h12l.94 1H5.12z",
    add: "M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z",
    close: "M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z",
    edit: "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z",
    delete: "M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z",
    check: "M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z",
    back: "M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z",
    info: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z",
    repeat: "M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z",
    warning: "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z",
    star: "M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z",
    gear: "M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z",
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <path d={paths[name] || ""} />
    </svg>
  );
}

// ─── SHARED UI ────────────────────────────────────────────────────────────────
const S = {
  card: { background: COLORS.surface, borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.07)", border: `1px solid ${COLORS.border}`, padding: "14px 16px", marginBottom: 10 },
  label: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: COLORS.textMuted, marginBottom: 4, display: "block" },
  input: { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid #e0e0e0`, background: "#fafafa", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box", color: COLORS.text },
  select: { width: "100%", padding: "10px 12px", borderRadius: 10, border: `1.5px solid #e0e0e0`, background: "#fafafa", fontSize: 15, fontFamily: "inherit", outline: "none", boxSizing: "border-box", color: COLORS.text },
};

function Btn({ children, onClick, variant = "primary", size = "md", style = {}, disabled = false }) {
  const variants = {
    primary: { background: `linear-gradient(135deg, #1a73e8, #1557b0)`, color: "#fff" },
    danger: { background: `linear-gradient(135deg, #e53935, #c62828)`, color: "#fff" },
    success: { background: `linear-gradient(135deg, #2e7d32, #1b5e20)`, color: "#fff" },
    amber: { background: `linear-gradient(135deg, #f57c00, #e65100)`, color: "#fff" },
    purple: { background: `linear-gradient(135deg, #7b1fa2, #4a148c)`, color: "#fff" },
    ghost: { background: "transparent", color: COLORS.blue, border: `1.5px solid ${COLORS.blue}` },
    outline: { background: "transparent", color: "#666", border: `1.5px solid #ddd` },
  };
  const sizes = { sm: { padding: "6px 12px", fontSize: 13 }, md: { padding: "10px 18px", fontSize: 15 }, lg: { padding: "13px 22px", fontSize: 16 } };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...variants[variant], ...sizes[size], borderRadius: 10, fontWeight: 600, fontFamily: "inherit", cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1, border: "none", display: "inline-flex", alignItems: "center", gap: 6, ...style }}
    >
      {children}
    </button>
  );
}

// Currency display: sign+BWP fixed left, number fixed-width right-aligned
function Amt({ n, color, size = 13, bold = false, showSign = false }) {
  const num = parseFloat(n) || 0;
  const negative = num < 0;
  const abs = Math.abs(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const c = color || (negative ? COLORS.red : COLORS.text);
  const fw = bold ? 800 : 600;
  const sign = showSign ? (negative ? "-" : "+") : (negative ? "-" : "");
  return (
    <span style={{ display: "inline-flex", alignItems: "baseline", width: 124, flexShrink: 0 }}>
      <span style={{ fontSize: size * 0.85, fontWeight: fw, color: c, opacity: 0.65, whiteSpace: "nowrap", width: 34, flexShrink: 0 }}>
        BWP
      </span>
      <span style={{ fontSize: size, fontWeight: fw, color: c, fontFamily: "monospace", textAlign: "right", flex: 1, whiteSpace: "nowrap" }}>
        {sign}{abs}
      </span>
    </span>
  );
}

function Badge({ label, color }) {
  const colors = { Paid: COLORS.green, Pending: "#f9a825", Late: COLORS.red, Pool: COLORS.blue, Personal: COLORS.purple, Credit: COLORS.blue, "Credit Used": COLORS.green };
  const bg = colors[color || label] || "#888";
  return (
    <span style={{ background: bg, color: "#fff", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>{label}</span>
  );
}

function Modal({ open, onClose, title, children, wide = false }) {
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1000, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: "0 8px 8px" }}>
      <div style={{ background: COLORS.surface, borderRadius: "18px 18px 14px 14px", width: "100%", maxWidth: wide ? 520 : 430, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.25)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 18px 12px", borderBottom: `1px solid ${COLORS.border}`, position: "sticky", top: 0, background: COLORS.surface, zIndex: 1 }}>
          <span style={{ fontWeight: 700, fontSize: 17, color: COLORS.text }}>{title}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "#999", padding: 4 }}><Icon name="close" size={20} /></button>
        </div>
        <div style={{ padding: "16px 18px 20px" }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, children, error }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && <span style={S.label}>{label}</span>}
      {children}
      {error && <span style={{ fontSize: 12, color: COLORS.red, marginTop: 3, display: "block" }}>{error}</span>}
    </div>
  );
}

function Avatar({ name, size = 34, bg = COLORS.blue }) {
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.42, fontWeight: 700, flexShrink: 0 }}>
      {(name || "?")[0].toUpperCase()}
    </div>
  );
}

// ─── ONBOARDING ───────────────────────────────────────────────────────────────
function Onboarding({ dispatch }) {
  const [step, setStep] = useState(1);
  const [adminName, setAdminName] = useState("");
  const [housemates, setHousemates] = useState(["", ""]);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [err, setErr] = useState("");

  function next1() {
    if (!adminName.trim()) { setErr("Please enter your name"); return; }
    setErr(""); setStep(2);
  }
  function next2() { setStep(3); }
  function confirm() {
    dispatch({ type: "SETUP", admin: adminName.trim(), members: housemates, month });
  }

  const filledHousemates = housemates.filter(h => h.trim()).length;

  return (
    <div style={{ position: "fixed", inset: 0, background: `linear-gradient(160deg, ${COLORS.navy} 0%, ${COLORS.navyLight} 100%)`, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "24px 20px", boxSizing: "border-box", overflowY: "auto" }}>
      <div style={{ color: COLORS.cyan, fontSize: 32, fontWeight: 800, marginBottom: 4, letterSpacing: -1 }}>HouseTab</div>
      <div style={{ color: "#aaa", fontSize: 14, marginBottom: 32 }}>Household expense manager</div>

      <div style={{ background: "rgba(255,255,255,0.06)", borderRadius: 20, padding: "28px 24px", width: "100%", maxWidth: 420, boxSizing: "border-box" }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 28, justifyContent: "center" }}>
          {[1, 2, 3].map(s => (
            <div key={s} style={{ width: s === step ? 28 : 8, height: 8, borderRadius: 4, background: s === step ? COLORS.cyan : "rgba(255,255,255,0.2)", transition: "all 0.3s" }} />
          ))}
        </div>

        {step === 1 && (
          <div>
            <div style={{ color: "#fff", fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Who are you?</div>
            <div style={{ color: "#aaa", fontSize: 13, marginBottom: 20 }}>You'll be set as the admin.</div>
            <input
              placeholder="Your name"
              value={adminName}
              onChange={e => setAdminName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && next1()}
              style={{ ...S.input, background: "rgba(255,255,255,0.1)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.15)", marginBottom: 8 }}
            />
            {err && <div style={{ color: COLORS.red, fontSize: 13, marginBottom: 8 }}>{err}</div>}
            <Btn onClick={next1} style={{ width: "100%", justifyContent: "center", marginTop: 8 }}>Continue</Btn>
          </div>
        )}

        {step === 2 && (
          <div>
            <div style={{ color: "#fff", fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Add housemates</div>
            <div style={{ color: "#aaa", fontSize: 13, marginBottom: 20 }}>Optional — you can add more later.</div>
            {housemates.map((h, i) => (
              <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                <input
                  placeholder={`Housemate ${i + 1}`}
                  value={h}
                  onChange={e => { const a = [...housemates]; a[i] = e.target.value; setHousemates(a); }}
                  style={{ ...S.input, background: "rgba(255,255,255,0.1)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.15)", flex: 1 }}
                />
                {housemates.length > 1 && (
                  <button onClick={() => setHousemates(housemates.filter((_, j) => j !== i))} style={{ background: "rgba(229,57,53,0.3)", border: "none", borderRadius: 10, color: "#ff8a80", cursor: "pointer", padding: "0 12px" }}>×</button>
                )}
              </div>
            ))}
            <button onClick={() => setHousemates([...housemates, ""])} style={{ background: "none", border: "1.5px dashed rgba(255,255,255,0.2)", borderRadius: 10, color: "#aaa", cursor: "pointer", padding: "8px 14px", fontSize: 13, width: "100%", marginBottom: 16 }}>+ Add row</button>
            <Btn onClick={next2} style={{ width: "100%", justifyContent: "center" }}>Continue</Btn>
          </div>
        )}

        {step === 3 && (
          <div>
            <div style={{ color: "#fff", fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Starting month</div>
            <div style={{ color: "#aaa", fontSize: 13, marginBottom: 20 }}>Choose the period to begin tracking.</div>
            <input type="month" value={month} onChange={e => setMonth(e.target.value)} style={{ ...S.input, background: "rgba(255,255,255,0.1)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.15)", marginBottom: 16 }} />
            <div style={{ background: "rgba(79,195,247,0.15)", border: "1px solid rgba(79,195,247,0.3)", borderRadius: 10, padding: "10px 14px", color: COLORS.cyan, fontSize: 13, marginBottom: 20 }}>
              {adminName} + {filledHousemates} housemate{filledHousemates !== 1 ? "s" : ""} · Starting {monthLabel(month)}
            </div>
            <Btn onClick={confirm} variant="success" style={{ width: "100%", justifyContent: "center" }}>
              <Icon name="check" size={18} /> Confirm & Start
            </Btn>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── OB MODAL ─────────────────────────────────────────────────────────────────
function OBModal({ open, onClose, state, dispatch }) {
  const [amount, setAmount] = useState(state.ob || "");
  const [note, setNote] = useState(state.obNote || "");
  useEffect(() => { if (open) { setAmount(state.ob || ""); setNote(state.obNote || ""); } }, [open]);
  function save() {
    dispatch({ type: "SET_OB", amount: parseFloat(amount) || 0, note });
    onClose();
  }
  return (
    <Modal open={open} onClose={onClose} title="Opening Balance">
      <div style={{ background: `${COLORS.cyan}18`, border: `1px solid ${COLORS.cyan}44`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#555", marginBottom: 16 }}>
        <Icon name="info" size={15} color={COLORS.cyan} /> Money in the pool before this period's contributions — e.g. rolled over balance or starting float.
      </div>
      <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 16 }}>Pool = OB + Cash + Personal − Actual Expenses</div>
      <Field label="Amount (BWP)">
        <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" style={S.input} />
      </Field>
      <Field label="Note (optional)">
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Rolled over from March" style={S.input} />
      </Field>
      <Btn onClick={save} style={{ width: "100%", justifyContent: "center", marginTop: 8 }}>Save Opening Balance</Btn>
    </Modal>
  );
}

// ─── CLOSE PERIOD MODAL ───────────────────────────────────────────────────────
function ClosePeriodModal({ open, onClose, state, dispatch, comp }) {
  const [step, setStep] = useState(1);
  const [action, setAction] = useState("roll");
  const [rollMode, setRollMode] = useState("shared"); // "shared" | "members"
  const [carryPersonal, setCarryPersonal] = useState("deduct");
  const [obRecipients, setObRecipients] = useState([]); // member ids who get OB credit
  const [activeMemberIds, setActiveMemberIds] = useState([]);
  const [newMemberName, setNewMemberName] = useState("");
  const [newMonth, setNewMonth] = useState("");

  useEffect(() => {
    if (open) {
      setStep(1);
      const activeIds = state.members.filter(m => m.active).map(m => m.id);
      setActiveMemberIds(activeIds);
      setObRecipients(activeIds);
      const cur = state.period?.month || new Date().toISOString().slice(0, 7);
      const [y, mo] = cur.split("-").map(Number);
      const next = mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, "0")}`;
      setNewMonth(next);
    }
  }, [open]);

  const { pool, cashPaid, actualTotal, personalTotal } = comp;
  const hasPersonal = comp.memberBalances.some(m => m.personal > 0);
  const totalSteps = action === "roll" && rollMode === "members" ? 4 : 3;

  function confirm() {
    const snap = {
      label: state.period.label, month: state.period.month, closedAt: today(),
      ob: state.ob, tc: cashPaid, ta: actualTotal, pool,
      action: action === "roll" ? "roll" : "dist",
      carryPersonal: action === "roll" ? carryPersonal : null,
      personalByMember: comp.memberBalances.map(m => ({ id: m.id, name: m.name, paid: m.personal })),
      expectedAmounts: [...(state.expectedAmounts || [])],
      contributions: [...state.contributions], expenses: [...state.expenses], members: [...state.members],
    };
    const carryPending = state.pendingSettlements || [];

    if (action === "roll") {
      let credits = [];
      // Personal carry credits
      if (hasPersonal && carryPersonal === "deduct") {
        credits = comp.memberBalances.filter(m => m.personal > 0).map(m => ({
          memberId: m.id, amount: m.personal, note: `Personal payment from ${state.period.label}`, fromPeriod: state.period.label,
        }));
      }
      if (rollMode === "members" && pool > 0) {
        // Per-member OB distribution: each selected member gets pool / recipients as credit
        const perHead = pool / obRecipients.length;
        const obCredits = obRecipients.map(id => ({
          memberId: id, amount: Math.round(perHead * 100) / 100,
          note: `OB share from ${state.period.label}`, fromPeriod: state.period.label,
        }));
        credits = [...credits, ...obCredits];
        // OB is set to 0 — credits replace it
        dispatch({ type: "CLOSE_ROLL", snap, month: newMonth, newOB: 0, newOBNote: "", active: activeMemberIds, credits, carryPending });
      } else {
        dispatch({ type: "CLOSE_ROLL", snap, month: newMonth, newOB: pool, newOBNote: `Rolled from ${state.period.label}`, active: activeMemberIds, credits, carryPending });
      }
    } else {
      dispatch({ type: "CLOSE_DIST", snap, month: newMonth, active: activeMemberIds, carryPending: [] });
    }
    onClose();
  }

  const perMember = action === "dist" && comp.active.length ? pool / comp.active.length : 0;
  const obPerRecipient = obRecipients.length > 0 ? pool / obRecipients.length : 0;

  // Step navigation — roll+members mode has an extra step between step 1 and members step
  function nextFromStep1() {
    if (action === "roll" && rollMode === "members") setStep(2); // go to OB recipients
    else setStep(action === "roll" && rollMode === "members" ? 2 : 2); // go straight to members
    setStep(2);
  }

  return (
    <Modal open={open} onClose={onClose} title="Close Period" wide>
      {/* Progress bar */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map(s => (
          <div key={s} style={{ flex: 1, height: 4, borderRadius: 4, background: s <= step ? COLORS.amber : "#eee", transition: "all 0.3s" }} />
        ))}
      </div>

      {/* ── Step 1: What to do with pool balance ── */}
      {step === 1 && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
            {[["Opening Bal", bwp(state.ob), COLORS.cyan], ["Contributions", bwp(cashPaid), COLORS.blue], ["Expenses", bwp(actualTotal), COLORS.red], ["Pool Balance", bwp(pool), pool >= 0 ? COLORS.green : COLORS.red]].map(([l, v, c]) => (
              <div key={l} style={{ ...S.card, textAlign: "center", margin: 0 }}>
                <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4 }}>{l}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: c }}>{v}</div>
              </div>
            ))}
          </div>

          <div style={{ fontWeight: 600, marginBottom: 12 }}>What happens to pool balance of <span style={{ color: pool >= 0 ? COLORS.green : COLORS.red }}>{bwp(pool)}</span>?</div>

          {/* Roll Over */}
          <div style={{ ...S.card, border: action === "roll" ? `2px solid ${COLORS.blue}` : `1px solid ${COLORS.border}`, marginBottom: 10, cursor: "pointer" }} onClick={() => setAction("roll")}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <input type="radio" checked={action === "roll"} onChange={() => setAction("roll")} style={{ marginTop: 3 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>Roll Over to Next Month</div>
                {action === "roll" && (
                  <div style={{ marginTop: 10 }}>
                    <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 10, cursor: "pointer" }}>
                      <input type="radio" checked={rollMode === "shared"} onChange={() => setRollMode("shared")} style={{ marginTop: 2 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>Shared OB — split equally among all next month's members</div>
                        <div style={{ fontSize: 11, color: COLORS.textMuted }}>Good when everyone contributed to the surplus</div>
                      </div>
                    </label>
                    <label style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
                      <input type="radio" checked={rollMode === "members"} onChange={() => setRollMode("members")} style={{ marginTop: 2 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>Distribute as credits to specific members</div>
                        <div style={{ fontSize: 11, color: COLORS.textMuted }}>Good when only some members should receive the surplus (e.g. new member joining next month)</div>
                      </div>
                    </label>
                    {action === "roll" && hasPersonal && (
                      <div style={{ marginTop: 12, padding: "10px 0", borderTop: `1px solid ${COLORS.border}` }}>
                        <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 6 }}>Members paid personally this month:</div>
                        <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6, cursor: "pointer" }}>
                          <input type="radio" checked={carryPersonal === "deduct"} onChange={() => setCarryPersonal("deduct")} />
                          <span style={{ fontSize: 13 }}>Deduct from next month (save credit per person)</span>
                        </label>
                        <label style={{ display: "flex", gap: 8, alignItems: "center", cursor: "pointer" }}>
                          <input type="radio" checked={carryPersonal === "letgo"} onChange={() => setCarryPersonal("letgo")} />
                          <span style={{ fontSize: 13 }}>Let it go (treat as gift)</span>
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Distribute Back */}
          <label style={{ display: "flex", gap: 10, alignItems: "flex-start", ...S.card, cursor: pool <= 0 ? "not-allowed" : "pointer", opacity: pool <= 0 ? 0.5 : 1, border: action === "dist" ? `2px solid ${COLORS.green}` : `1px solid ${COLORS.border}`, marginBottom: 16 }}>
            <input type="radio" checked={action === "dist"} onChange={() => pool > 0 && setAction("dist")} disabled={pool <= 0} style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 600 }}>Distribute Back Equally</div>
              <div style={{ fontSize: 13, color: COLORS.textMuted }}>{pool > 0 ? `${bwp(perMember)} per member · Pool resets to zero` : "Not available — pool is zero or in deficit"}</div>
            </div>
          </label>

          <Btn onClick={() => setStep(2)} style={{ width: "100%", justifyContent: "center" }} variant="amber">Next</Btn>
        </div>
      )}

      {/* ── Step 2: OB recipients (only when rollMode === "members") ── */}
      {step === 2 && action === "roll" && rollMode === "members" && (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Who receives the OB credit?</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 14 }}>
            {bwp(pool)} will be split equally among selected members as carry-forward credits. Each gets {bwp(obPerRecipient)}.
          </div>
          {state.members.filter(m => m.active).map(m => {
            const checked = obRecipients.includes(m.id);
            return (
              <label key={m.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 12px", borderRadius: 10, marginBottom: 8, cursor: "pointer", background: checked ? `${COLORS.blue}10` : COLORS.bg, border: `1.5px solid ${checked ? COLORS.blue : "#e0e0e0"}` }}>
                <input type="checkbox" checked={checked} onChange={e => {
                  if (e.target.checked) setObRecipients([...obRecipients, m.id]);
                  else setObRecipients(obRecipients.filter(id => id !== m.id));
                }} style={{ width: 16, height: 16, accentColor: COLORS.blue }} />
                <Avatar name={m.name} size={28} bg={checked ? COLORS.blue : "#bbb"} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{m.name}</div>
                  {checked && <div style={{ fontSize: 11, color: COLORS.blue }}>Gets {bwp(obPerRecipient)} credit</div>}
                </div>
              </label>
            );
          })}
          {obRecipients.length === 0 && (
            <div style={{ fontSize: 12, color: COLORS.red, marginBottom: 8 }}>Select at least one member to receive the credit.</div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Btn onClick={() => setStep(1)} variant="outline" style={{ flex: 1, justifyContent: "center" }}>Back</Btn>
            <Btn onClick={() => setStep(3)} variant="amber" disabled={obRecipients.length === 0} style={{ flex: 2, justifyContent: "center" }}>Next: Members</Btn>
          </div>
        </div>
      )}

      {/* ── Step 3 (or 2 for non-member-dist): Active members next period ── */}
      {((step === 3 && action === "roll" && rollMode === "members") || (step === 2 && !(action === "roll" && rollMode === "members"))) && (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Active members next month</div>
          {state.members.map(m => (
            <label key={m.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer" }}>
              <input type="checkbox" checked={activeMemberIds.includes(m.id)} onChange={e => {
                if (e.target.checked) setActiveMemberIds([...activeMemberIds, m.id]);
                else setActiveMemberIds(activeMemberIds.filter(id => id !== m.id));
              }} />
              <Avatar name={m.name} size={28} />
              <span style={{ fontWeight: 500 }}>{m.name}</span>
              {m.id === state.admin && <Badge label="Admin" color={COLORS.blue} />}
            </label>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <input placeholder="New member name" value={newMemberName} onChange={e => setNewMemberName(e.target.value)} style={{ ...S.input, flex: 1 }} />
            <Btn size="sm" onClick={() => {
              if (newMemberName.trim()) {
                const newM = { id: uid(), name: newMemberName.trim(), active: true, createdAt: today() };
                dispatch({ type: "ADD_MEMBER", member: newM });
                setActiveMemberIds(a => [...a, newM.id]);
                setNewMemberName("");
              }
            }}>Add</Btn>
          </div>
          <Field label="New period" style={{ marginTop: 16 }}>
            <input type="month" value={newMonth} onChange={e => setNewMonth(e.target.value)} style={{ ...S.input, marginTop: 8 }} />
          </Field>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <Btn onClick={() => setStep(action === "roll" && rollMode === "members" ? 2 : 1)} variant="outline" style={{ flex: 1, justifyContent: "center" }}>Back</Btn>
            <Btn onClick={() => setStep(action === "roll" && rollMode === "members" ? 4 : 3)} variant="amber" style={{ flex: 2, justifyContent: "center" }}>Next: Review</Btn>
          </div>
        </div>
      )}

      {/* ── Final review step ── */}
      {((step === 4 && action === "roll" && rollMode === "members") || (step === 3 && !(action === "roll" && rollMode === "members"))) && (
        <div>
          <div style={{ background: COLORS.navy, borderRadius: 14, padding: "18px 16px", marginBottom: 16, color: "#fff" }}>
            <div style={{ fontSize: 13, color: "#aaa", marginBottom: 12 }}>Closing Summary</div>
            {[
              ["Closing Period", state.period.label],
              ["Starting Period", monthLabel(newMonth)],
              ["Action", action === "roll" ? (rollMode === "members" ? "Credit to members" : "Roll Over") : "Distribute Back"],
              [action === "roll" && rollMode === "members" ? "Credits" : "New OB / Per Member",
               action === "roll" && rollMode === "members"
                 ? `${bwp(obPerRecipient)} × ${obRecipients.length} member${obRecipients.length !== 1 ? "s" : ""}`
                 : action === "roll" ? bwp(pool) : bwp(perMember)],
              ["Active Members", activeMemberIds.length],
            ].map(([l, v]) => (
              <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 14 }}>
                <span style={{ color: "#aaa" }}>{l}</span>
                <span style={{ fontWeight: 600, color: COLORS.cyan }}>{v}</span>
              </div>
            ))}
            {action === "roll" && rollMode === "members" && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                <div style={{ fontSize: 12, color: "#aaa", marginBottom: 6 }}>OB credit recipients:</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {obRecipients.map(id => {
                    const m = state.members.find(x => x.id === id);
                    return m ? <div key={id} style={{ background: `${COLORS.blue}40`, color: COLORS.cyan, borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 600 }}>{m.name}</div> : null;
                  })}
                </div>
              </div>
            )}
          </div>
          <div style={{ background: `${COLORS.amber}18`, border: `1px solid ${COLORS.amber}44`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: COLORS.amber, marginBottom: 16 }}>
            <Icon name="warning" size={14} /> This archives the current period and resets contributions and expenses. Cannot be undone.
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
            {activeMemberIds.map(id => {
              const m = state.members.find(x => x.id === id);
              return m ? <div key={id} style={{ background: `${COLORS.blue}18`, color: COLORS.blue, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>{m.name}</div> : null;
            })}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={() => setStep(action === "roll" && rollMode === "members" ? 3 : 2)} variant="outline" style={{ flex: 1, justifyContent: "center" }}>Back</Btn>
            <Btn onClick={confirm} variant="amber" style={{ flex: 2, justifyContent: "center" }}>
              <Icon name="archive" size={16} /> Close Period
            </Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── SCREEN 1: DASHBOARD ──────────────────────────────────────────────────────
function Dashboard({ state, dispatch, comp }) {
  const [obOpen, setObOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const { cashPaid, combinedCash, actualTotal, pool, grossPool, projected, personalTotal } = comp;
  const settledPayouts = (state.transferLogs || []).filter(t => t.fromPool).reduce((s, t) => s + t.amount, 0);
  const showOB = (state.settings || {}).showOBOnDash !== false;

  // Compute active reminder alerts
  const todayDate = new Date();
  const todayDay = todayDate.getDate();
  const reminderAlerts = (state.reminders || []).filter(r => r.active !== false).map(r => {
    const daysUntil = r.dayOfMonth >= todayDay
      ? r.dayOfMonth - todayDay
      : new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, r.dayOfMonth).getDate() +
        (new Date(todayDate.getFullYear(), todayDate.getMonth() + 1, 0).getDate() - todayDay);
    const warn = r.daysBeforeWarn || 3;
    const isDue = daysUntil === 0;
    const isOverdue = r.dayOfMonth < todayDay && daysUntil > 20;
    const isSoon = daysUntil <= warn && daysUntil > 0;
    return { ...r, daysUntil, isDue, isSoon, isOverdue };
  }).filter(r => r.isDue || r.isSoon || r.isOverdue);

  const unpaidMembers = state.members.filter(m => {
    if (!m.active) return false;
    const mb = comp.memberBalances.find(x => x.id === m.id);
    if (mb && mb.expected !== null) {
      // Has expected set: unpaid if paid cash + credit < expected
      return (mb.cash + (mb.credit || 0)) < mb.expected;
    }
    // No expected set: unpaid if no Paid contribution at all
    const last = state.contributions.filter(c => c.memberId === m.id && c.status === "Paid");
    return last.length === 0;
  });
  const upcoming = state.expenses.filter(e => e.type === "planned").sort((a, b) => (a.dueDate || "") > (b.dueDate || "") ? 1 : -1).slice(0, 4);

  return (
    <div>
      {/* Reminder alerts */}
      {reminderAlerts.map(r => (
        <div key={r.id} style={{ background: r.isDue ? `${COLORS.red}15` : `${COLORS.amber}15`, border: `1px solid ${r.isDue ? COLORS.red : COLORS.amber}55`, borderRadius: 12, padding: "10px 14px", marginBottom: 10, display: "flex", alignItems: "center", gap: 10 }}>
          <Icon name="warning" size={18} color={r.isDue ? COLORS.red : COLORS.amber} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: r.isDue ? COLORS.red : COLORS.amber }}>{r.title}</div>
            <div style={{ fontSize: 11, color: COLORS.textMuted }}>
              {r.isDue ? "Due today!" : r.isOverdue ? "Overdue" : `Due in ${r.daysUntil} day${r.daysUntil !== 1 ? "s" : ""} (day ${r.dayOfMonth})`}
            </div>
          </div>
        </div>
      ))}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 18, color: COLORS.text }}>{state.period?.label}</div>
        <Btn variant="amber" size="sm" onClick={() => setCloseOpen(true)}><Icon name="archive" size={15} /> Close Month</Btn>
      </div>

      {/* OB Hero */}
      {showOB && (
      <div onClick={() => setObOpen(true)} style={{ background: COLORS.navy, borderRadius: 16, padding: "18px 18px", marginBottom: 12, cursor: "pointer" }}>
        <div style={{ fontSize: 11, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6 }}>Opening Balance</div>
        {state.ob > 0 ? (
          <>
            <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.cyan }}>{bwp(state.ob)}</div>
            {state.obNote && <div style={{ fontSize: 13, color: "#aaa", fontStyle: "italic", marginTop: 4 }}>{state.obNote}</div>}
          </>
        ) : (
          <div style={{ fontSize: 15, color: "#777" }}>Tap to set opening balance</div>
        )}
        <div style={{ fontSize: 12, color: "#555", marginTop: 6 }}>Tap to edit</div>
      </div>
      )}
      {/* 4 stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
        {[
          { label: "Contributions", value: bwp(combinedCash), color: COLORS.blue, sub: personalTotal > 0 ? `incl. ${bwp(personalTotal)} personal` : null },
          { label: "Actual Spent", value: bwp(actualTotal), color: COLORS.red },
          { label: "Pool Balance", value: bwp(pool), color: pool >= 0 ? COLORS.green : COLORS.red, sub: pool < 0 ? "⚠ Deficit" : `OB ${bwp(state.ob)} included` },
          { label: "Projected", value: bwp(projected), color: projected >= 0 ? COLORS.purple : COLORS.amber },
        ].map(({ label, value, color, sub }) => (
          <div key={label} style={{ ...S.card, margin: 0 }}>
            <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 4, fontWeight: 600 }}>{label}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color }}>{value}</div>
            {sub && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{sub}</div>}
          </div>
        ))}
      </div>

      {/* Collection Target */}
      {comp.totalExpectedSet > 0 && (
        <div style={{ ...S.card, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={S.label}>Collection Target</span>
            <span style={{ fontSize: 12, color: COLORS.textMuted }}>{comp.totalExpectedSet}/{comp.active.length} set</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 13, color: "#555" }}>Expected total</span>
            <span style={{ fontWeight: 700, color: COLORS.blue }}>{bwp(comp.totalExpected)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: "#555" }}>Collected (paid)</span>
            <span style={{ fontWeight: 700, color: COLORS.green }}>{bwp(cashPaid)}</span>
          </div>
          <div style={{ background: "#f0f0f0", borderRadius: 6, height: 8, overflow: "hidden", marginBottom: 6 }}>
            <div style={{ width: `${Math.min((cashPaid / Math.max(comp.totalExpected, 1)) * 100, 100)}%`, height: "100%", background: cashPaid >= comp.totalExpected ? `linear-gradient(90deg,${COLORS.green},#43a047)` : `linear-gradient(90deg,${COLORS.blue},${COLORS.cyan})`, borderRadius: 6, transition: "width 0.5s" }} />
          </div>
          {cashPaid < comp.totalExpected && (
            <div style={{ fontSize: 12, color: COLORS.amber }}>Still need {bwp(comp.totalExpected - cashPaid)}</div>
          )}
          {cashPaid >= comp.totalExpected && comp.totalExpected > 0 && (
            <div style={{ fontSize: 12, color: COLORS.green }}>✓ Collection target met</div>
          )}
        </div>
      )}

      {/* Waterfall */}
      <div style={S.card}>
        <div style={S.label}>Pool Breakdown</div>
        {[
          ["+ Opening Balance", state.ob, COLORS.cyan],
          ["+ Cash Contributions", cashPaid, COLORS.blue],
          ["+ Personal Payments", personalTotal, COLORS.purple],
          ["− Actual Expenses", actualTotal, COLORS.red],
        ].map(([l, v, c]) => (
          <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 13, color: "#555", flex: 1 }}>{l}</span>
            <Amt n={v} color={c} bold />
          </div>
        ))}
        {settledPayouts > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 13, color: "#555", flex: 1 }}>− Settled Payouts</span>
            <Amt n={settledPayouts} color={COLORS.green} bold />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
          <span style={{ fontWeight: 800, flex: 1 }}>= Pool Balance</span>
          <Amt n={pool} color={pool >= 0 ? COLORS.green : COLORS.red} bold size={15} />
        </div>
      </div>

      {/* Deficit alert */}
      {pool < 0 && (
        <div style={{ background: `${COLORS.red}18`, border: `1px solid ${COLORS.red}44`, borderRadius: 12, padding: "12px 14px", marginBottom: 12, color: COLORS.red, fontSize: 13 }}>
          <Icon name="warning" size={15} /> <strong>Pool Deficit: {bwp(Math.abs(pool))}</strong> — Roll over to carry as negative opening balance next month.
        </div>
      )}

      {/* Unpaid members */}
      {unpaidMembers.length > 0 && (
        <div style={S.card}>
          <div style={S.label}>Unpaid / Shortfall This Month</div>
          {unpaidMembers.map(m => {
            const mb = comp.memberBalances.find(x => x.id === m.id);
            const shortfall = mb && mb.expected !== null ? mb.expected - mb.cash : null;
            return (
              <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                <Avatar name={m.name} size={28} bg={COLORS.red} />
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 500 }}>{m.name}</span>
                  {shortfall !== null && shortfall > 0 && <div style={{ fontSize: 11, color: COLORS.amber }}>Paid {bwp(mb.cash)} of {bwp(mb.expected)}</div>}
                </div>
                {shortfall !== null && shortfall > 0
                  ? <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.amber }}>−{bwp(shortfall)}</span>
                  : <Badge label="Pending" />}
              </div>
            );
          })}
        </div>
      )}

      {/* Upcoming planned */}
      {upcoming.length > 0 && (
        <div style={S.card}>
          <div style={S.label}>Upcoming Planned</div>
          {upcoming.map(e => (
            <div key={e.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}>
              <div>
                <div style={{ fontWeight: 500 }}>{e.title}</div>
                {e.dueDate && <div style={{ color: COLORS.textMuted, fontSize: 11 }}>Due {e.dueDate}</div>}
              </div>
              <span style={{ fontWeight: 700, color: COLORS.amber }}>{bwp(e.amount)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Closed periods */}
      {state.archive.length > 0 && (
        <div style={S.card}>
          <div style={S.label}>Closed Periods</div>
          {state.archive.map((a, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{a.label}</div>
                <div style={{ color: COLORS.textMuted, fontSize: 11 }}>Closed {a.closedAt} · {a.action === "roll" ? "Rolled" : "Distributed"}</div>
              </div>
              <span style={{ fontWeight: 700, color: a.pool >= 0 ? COLORS.green : COLORS.red }}>{bwp(a.pool)}</span>
            </div>
          ))}
        </div>
      )}

      <OBModal open={obOpen} onClose={() => setObOpen(false)} state={state} dispatch={dispatch} />
      <ClosePeriodModal open={closeOpen} onClose={() => setCloseOpen(false)} state={state} dispatch={dispatch} comp={comp} />
    </div>
  );
}

// ─── SCREEN 2: MEMBERS ────────────────────────────────────────────────────────
function Members({ state, dispatch, comp }) {
  const [addOpen, setAddOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [transferKeep, setTransferKeep] = useState(true);
  const adminMember = state.members.find(m => m.id === state.admin);

  function addMember() {
    if (!newName.trim()) return;
    dispatch({ type: "ADD_MEMBER", member: { id: uid(), name: newName.trim(), active: true, createdAt: today() } });
    setNewName(""); setAddOpen(false);
  }
  function doTransfer() {
    if (!transferTo) return;
    dispatch({ type: "TRANSFER", to: transferTo, keep: transferKeep });
    setTransferOpen(false);
  }

  return (
    <div>
      {adminMember && (
        <div style={{ background: `${COLORS.blue}18`, border: `1px solid ${COLORS.blue}33`, borderRadius: 12, padding: "10px 14px", marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontSize: 13, color: COLORS.blue, fontWeight: 600 }}>Admin: {adminMember.name}</div>
          <div style={{ fontSize: 12, color: COLORS.textMuted }}>{comp.active.length} active</div>
        </div>
      )}

      {state.members.map(m => (
        <div key={m.id} style={{ ...S.card, display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar name={m.name} size={40} bg={m.id === state.admin ? COLORS.blue : m.active ? COLORS.navy : "#bbb"} />
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              <span style={{ fontWeight: 600 }}>{m.name}</span>
              {m.id === state.admin && <Badge label="ADMIN" color={COLORS.blue} />}
              <Badge label={m.active ? "Active" : "Removed"} color={m.active ? COLORS.green : "#999"} />
            </div>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 2 }}>Joined {m.createdAt}</div>
          </div>
          {m.id !== state.admin && (
            <Btn size="sm" variant={m.active ? "outline" : "ghost"} onClick={() => dispatch({ type: "TOGGLE_MEMBER", id: m.id })}>
              {m.active ? "Remove" : "Restore"}
            </Btn>
          )}
        </div>
      ))}

      <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
        <Btn onClick={() => setAddOpen(true)} style={{ flex: 1, justifyContent: "center" }}><Icon name="add" size={16} /> Add Member</Btn>
        <Btn onClick={() => setTransferOpen(true)} variant="ghost" style={{ flex: 1, justifyContent: "center" }}>Transfer Admin</Btn>
      </div>

      {state.adminHistory.length > 0 && (
        <div style={{ ...S.card, marginTop: 14 }}>
          <div style={S.label}>Admin History</div>
          {state.adminHistory.map((h, i) => {
            const m = state.members.find(x => x.id === h.memberId);
            return (
              <div key={i} style={{ fontSize: 13, padding: "5px 0", borderBottom: `1px solid ${COLORS.border}`, color: COLORS.textMuted }}>
                {h.date} — {m?.name || "Unknown"} · {h.action} {h.keep !== undefined ? (h.keep ? "(with data)" : "(without data)") : ""}
              </div>
            );
          })}
        </div>
      )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Member">
        <Field label="Name">
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Member name" style={S.input} />
        </Field>
        <Btn onClick={addMember} style={{ width: "100%", justifyContent: "center" }}>Add Member</Btn>
      </Modal>

      <Modal open={transferOpen} onClose={() => setTransferOpen(false)} title="Transfer Admin">
        <div style={{ background: `${COLORS.red}18`, border: `1px solid ${COLORS.red}44`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: COLORS.red, marginBottom: 16 }}>
          <Icon name="warning" size={14} /> This transfer is permanent and immediate.
        </div>
        <Field label="New Admin">
          <select value={transferTo} onChange={e => setTransferTo(e.target.value)} style={S.select}>
            <option value="">Select member…</option>
            {state.members.filter(m => m.active && m.id !== state.admin).map(m => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </Field>
        <div style={{ marginBottom: 16 }}>
          {[["With Data", true, "Keep all history and finances"], ["Without Data", false, "Reset finances, keep members"]].map(([l, v, d]) => (
            <label key={l} style={{ display: "flex", gap: 10, alignItems: "center", padding: "8px 0", cursor: "pointer" }}>
              <input type="radio" checked={transferKeep === v} onChange={() => setTransferKeep(v)} />
              <div><div style={{ fontWeight: 600 }}>{l}</div><div style={{ fontSize: 12, color: COLORS.textMuted }}>{d}</div></div>
            </label>
          ))}
        </div>
        <Btn onClick={doTransfer} variant="danger" disabled={!transferTo} style={{ width: "100%", justifyContent: "center" }}>Transfer Admin</Btn>
      </Modal>
    </div>
  );
}

// ─── EXPECTED AMOUNT MODAL ────────────────────────────────────────────────────
function ExpectedModal({ open, onClose, member, state, dispatch }) {
  const existing = member ? (state.expectedAmounts || []).find(e => e.memberId === member.id) : null;
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (open && member) {
      setAmount(existing ? String(existing.amount) : "");
      setNote(existing ? existing.note : "");
    }
  }, [open, member]);
  function save() {
    if (!member) return;
    dispatch({ type: "SET_EXPECTED", memberId: member.id, amount: parseFloat(amount) || 0, note });
    onClose();
  }
  if (!member) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Expected — ${member.name}`}>
      <div style={{ background: `${COLORS.blue}12`, border: `1px solid ${COLORS.blue}33`, borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#555", marginBottom: 16 }}>
        Set the amount this member is expected to contribute this period. They can top up with multiple payments until they reach this target.
      </div>
      <Field label="Expected Amount (BWP)">
        <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" style={S.input} />
      </Field>
      <Field label="Reason / Note (optional)">
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Away for 2 weeks, no groceries" style={S.input} />
      </Field>
      {existing && (
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 12 }}>Last updated {existing.updatedAt}</div>
      )}
      <Btn onClick={save} style={{ width: "100%", justifyContent: "center" }} disabled={!amount}>Save Expected Amount</Btn>
    </Modal>
  );
}

// ─── SCREEN 3: CONTRIBUTIONS ──────────────────────────────────────────────────
function Contributions({ state, dispatch, comp }) {
  const [addOpen, setAddOpen] = useState(false);
  const [expectedOpen, setExpectedOpen] = useState(false);
  const [expectedMember, setExpectedMember] = useState(null);
  const [form, setForm] = useState({ memberId: "", amount: "", date: today(), status: "Paid" });
  const statusCycle = { Paid: "Pending", Pending: "Late", Late: "Paid" };
  const { cashPaid, combinedCash, personalTotal, memberBalances } = comp;
  const pending = state.contributions.filter(c => c.status !== "Paid").reduce((s, c) => s + c.amount, 0);

  function addContrib() {
    if (!form.memberId || !form.amount) return;
    dispatch({ type: "ADD_CONTRIB", contrib: { id: uid(), memberId: form.memberId, amount: parseFloat(form.amount), date: form.date, status: form.status } });
    setForm({ memberId: "", amount: "", date: today(), status: "Paid" });
    setAddOpen(false);
  }

  const personalExps = state.expenses.filter(e => e.type === "actual" && e.paidBy !== "pool");
  const sorted = [...state.contributions].sort((a, b) => b.date > a.date ? 1 : -1);

  return (
    <div>
      {/* Totals row — 3 cards: Cash | Personal | Total */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 14 }}>
        <div style={{ ...S.card, margin: 0, textAlign: "center", padding: "10px 6px" }}>
          <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 3, fontWeight: 600 }}>Cash Paid</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: COLORS.green }}>{bwp(cashPaid)}</div>
        </div>
        <div style={{ ...S.card, margin: 0, textAlign: "center", padding: "10px 6px" }}>
          <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 3, fontWeight: 600 }}>Personal</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: COLORS.purple }}>{bwp(personalTotal)}</div>
        </div>
        <div style={{ ...S.card, margin: 0, textAlign: "center", padding: "10px 6px", background: COLORS.navy }}>
          <div style={{ fontSize: 10, color: "#aaa", marginBottom: 3, fontWeight: 600 }}>Total</div>
          <div style={{ fontSize: 14, fontWeight: 800, color: COLORS.cyan }}>{bwp(combinedCash)}</div>
        </div>
      </div>

      {/* Per-member expected vs paid cards */}
      <div style={S.label}>Expected vs Paid</div>
      {memberBalances.map(m => {
        const memberContribs = sorted.filter(c => c.memberId === m.id);
        const paidCash = memberContribs.filter(c => c.status === "Paid").reduce((s, c) => s + c.amount, 0);
        const creditAmt = m.credit || 0;
        const effectivePaid = paidCash + creditAmt;
        const hasPendingContrib = memberContribs.some(c => c.status !== "Paid");
        const hasExpected = m.expected !== null;
        const pct = hasExpected && m.expected > 0 ? Math.min((effectivePaid / m.expected) * 100, 100) : null;
        const shortfall = hasExpected ? m.expected - effectivePaid : null;
        const met = hasExpected && effectivePaid >= m.expected;
        const over = hasExpected && effectivePaid > m.expected;

        return (
          <div key={m.id} style={{ ...S.card, margin: "0 0 10px", border: met ? `1.5px solid ${COLORS.green}44` : hasPendingContrib ? `1.5px solid ${COLORS.amber}44` : `1px solid ${COLORS.border}` }}>
            {/* Header row */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <Avatar name={m.name} size={36} bg={met ? COLORS.green : hasExpected ? COLORS.blue : COLORS.navy} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{m.name}</div>
                {m.expectedNote && <div style={{ fontSize: 11, color: COLORS.textMuted, fontStyle: "italic" }}>{m.expectedNote}</div>}
              </div>
              <button
                onClick={() => { setExpectedMember(m); setExpectedOpen(true); }}
                style={{ background: `${COLORS.blue}15`, border: "none", borderRadius: 8, cursor: "pointer", padding: "5px 8px", color: COLORS.blue, fontSize: 11, fontWeight: 600, fontFamily: "inherit" }}
              >
                {hasExpected ? "Edit" : "Set Expected"}
              </button>
            </div>

            {/* Expected / Paid amounts — 2×2 grid, never overflows */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: hasExpected ? 10 : 0 }}>
              <div style={{ background: COLORS.bg, borderRadius: 10, padding: "8px 10px" }}>
                <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 2, fontWeight: 600 }}>EXPECTED</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: hasExpected ? COLORS.blue : "#ccc" }}>
                  {hasExpected ? bwp(m.expected) : "—"}
                </div>
              </div>
              <div style={{ background: COLORS.bg, borderRadius: 10, padding: "8px 10px" }}>
                <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 2, fontWeight: 600 }}>CASH PAID</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: paidCash > 0 ? COLORS.green : "#ccc" }}>
                  {paidCash > 0 ? bwp(paidCash) : "—"}
                </div>
                {creditAmt > 0 && <div style={{ fontSize: 10, color: COLORS.blue, marginTop: 2 }}>+{bwp(creditAmt)} credit</div>}
              </div>
              {m.personal > 0 && (
                <div style={{ background: `${COLORS.purple}12`, borderRadius: 10, padding: "8px 10px", gridColumn: "1 / -1" }}>
                  <div style={{ fontSize: 10, color: COLORS.purple, marginBottom: 2, fontWeight: 600 }}>PERSONAL PAYMENT</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: COLORS.purple }}>{bwp(m.personal)}</div>
                  <div style={{ fontSize: 10, color: COLORS.purple, opacity: 0.7 }}>Paid household expense out of pocket</div>
                </div>
              )}
            </div>

            {/* Progress bar */}
            {hasExpected && m.expected > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ background: "#f0f0f0", borderRadius: 6, height: 8, overflow: "hidden", marginBottom: 5 }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: met ? `linear-gradient(90deg,${COLORS.green},#43a047)` : `linear-gradient(90deg,${COLORS.blue},${COLORS.cyan})`, borderRadius: 6, transition: "width 0.4s" }} />
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: met ? COLORS.green : shortfall > 0 ? COLORS.amber : COLORS.textMuted }}>
                  {met
                    ? over
                      ? `✓ Paid · ${bwp(effectivePaid - m.expected)} over`
                      : "✓ Fully paid"
                    : shortfall > 0
                      ? `Still needs ${bwp(shortfall)}${creditAmt > 0 ? " (after credit)" : ""}`
                      : "No payments yet"}
                </div>
              </div>
            )}

            {/* Individual contribution rows */}
            {memberContribs.length > 0 && (
              <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 8, marginTop: 4 }}>
                {memberContribs.map(c => (
                  <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                    <div style={{ flex: 1, fontSize: 13, color: COLORS.textMuted }}>{c.date}</div>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{bwp(c.amount)}</span>
                    <div onClick={() => dispatch({ type: "UPD_CONTRIB", id: c.id, status: statusCycle[c.status] })} style={{ cursor: "pointer" }}><Badge label={c.status} /></div>
                    <button onClick={() => dispatch({ type: "DEL_CONTRIB", id: c.id })} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.red, padding: 2 }}><Icon name="delete" size={16} /></button>
                  </div>
                ))}
              </div>
            )}
            {memberContribs.length === 0 && (
              <div style={{ fontSize: 12, color: COLORS.textMuted, fontStyle: "italic" }}>No payments yet</div>
            )}
          </div>
        );
      })}

      {/* Credits */}
      {(state.credits || []).length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={S.label}>Carry-forward Credits</div>
          {state.credits.map((cr, i) => {
            const m = state.members.find(x => x.id === cr.memberId);
            const hasPaid = state.contributions.some(c => c.memberId === cr.memberId && c.status === "Paid");
            return (
              <div key={i} style={{ ...S.card, border: `2px solid ${COLORS.blue}44`, margin: "0 0 8px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <Avatar name={m?.name || "?"} size={28} />
                    <div>
                      <span style={{ fontWeight: 600 }}>{m?.name}</span>{" "}
                      <Badge label={hasPaid ? "CREDIT USED" : "CREDIT"} color={hasPaid ? COLORS.green : COLORS.blue} />
                    </div>
                  </div>
                  <span style={{ color: COLORS.blue, fontWeight: 700 }}>−{bwp(cr.amount)}</span>
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 4 }}>from {cr.fromPeriod} {hasPaid ? "✓ settled" : ""}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Personal payments */}
      {personalExps.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <div style={S.label}>Personal Payments (counted as contributions)</div>
          {personalExps.map(e => {
            const m = state.members.find(x => x.id === e.paidBy);
            return (
              <div key={e.id} style={{ ...S.card, borderLeft: `3px solid ${COLORS.purple}`, margin: "0 0 8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar name={m?.name || "?"} size={30} bg={COLORS.purple} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{m?.name}</div>
                    <div style={{ fontSize: 12, color: COLORS.textMuted }}>{e.paidDate || e.dueDate} · {e.title}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, color: COLORS.purple }}>{bwp(e.amount)}</div>
                    <Badge label="Personal" color={COLORS.purple} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Settlement transfer log */}
      {(state.transferLogs || []).length > 0 && (
        <div style={{ marginTop: 4 }}>
          <div style={S.label}>Settlement Transfers</div>
          {(state.transferLogs || []).map((t, i) => (
            <div key={t.id} style={{ ...S.card, borderLeft: `3px solid ${t.fromPool ? COLORS.cyan : COLORS.blue}`, margin: "0 0 8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    <span style={{ color: t.fromPool ? COLORS.cyan : COLORS.blue }}>{t.from}</span>
                    <span style={{ color: COLORS.textMuted }}> → </span>
                    <span style={{ color: COLORS.green }}>{t.to}</span>
                  </div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 2 }}>{t.date} · {t.fromPool ? "Pool payout" : "Member transfer"}</div>
                </div>
                <span style={{ fontWeight: 700, color: COLORS.text }}>{bwp(t.amount)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Btn onClick={() => setAddOpen(true)} style={{ width: "100%", justifyContent: "center", marginTop: 8 }}><Icon name="add" size={16} /> Add Contribution</Btn>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add Contribution">
        <Field label="Member">
          <select value={form.memberId} onChange={e => {
            const memberId = e.target.value;
            const mb = comp.memberBalances.find(x => x.id === memberId);
            const effectivePaid = mb ? (mb.cash + (mb.credit || 0)) : 0;
            const shortfall = mb && mb.expected !== null ? Math.max(mb.expected - effectivePaid, 0) : 0;
            setForm({ ...form, memberId, amount: shortfall > 0 ? String(shortfall) : "" });
          }} style={S.select}>
            <option value="">Select member…</option>
            {state.members.filter(m => m.active).map(m => {
              const mb = comp.memberBalances.find(x => x.id === m.id);
              const effectivePaid = mb ? (mb.cash + (mb.credit || 0)) : 0;
              const shortfall = mb && mb.expected !== null ? mb.expected - effectivePaid : null;
              return (
                <option key={m.id} value={m.id}>
                  {m.name}{shortfall !== null && shortfall > 0 ? ` (owes ${bwp(shortfall)})` : ""}
                </option>
              );
            })}
          </select>
        </Field>
        {form.memberId && (() => {
          const mb = comp.memberBalances.find(x => x.id === form.memberId);
          if (mb && mb.expected !== null) {
            const effectivePaid = mb.cash + (mb.credit || 0);
            const shortfall = mb.expected - effectivePaid;
            return (
              <div style={{ background: shortfall > 0 ? `${COLORS.amber}18` : `${COLORS.green}18`, border: `1px solid ${shortfall > 0 ? COLORS.amber : COLORS.green}44`, borderRadius: 10, padding: "8px 12px", fontSize: 13, marginBottom: 12 }}>
                Expected: <strong>{bwp(mb.expected)}</strong> · Paid: <strong>{bwp(effectivePaid)}</strong>
                {mb.credit > 0 && <span style={{ color: COLORS.blue }}> (incl. {bwp(mb.credit)} credit)</span>}
                {shortfall > 0 ? <span style={{ color: COLORS.amber }}> · Still needs {bwp(shortfall)}</span> : <span style={{ color: COLORS.green }}> · ✓ Fully paid</span>}
              </div>
            );
          }
          return null;
        })()}
        <Field label="Amount (BWP)">
          <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0.00" style={S.input} />
        </Field>
        <Field label="Date">
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} style={S.input} />
        </Field>
        <Field label="Status">
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={S.select}>
            <option>Paid</option><option>Pending</option><option>Late</option>
          </select>
        </Field>
        <Btn onClick={addContrib} style={{ width: "100%", justifyContent: "center" }} disabled={!form.memberId || !form.amount}>Add Contribution</Btn>
      </Modal>

      <ExpectedModal open={expectedOpen} onClose={() => setExpectedOpen(false)} member={expectedMember} state={state} dispatch={dispatch} />
    </div>
  );
}

// ─── SCREEN 4: EXPENSES ───────────────────────────────────────────────────────
function Expenses({ state, dispatch, comp }) {
  const [tab, setTab] = useState("actual");
  const [addOpen, setAddOpen] = useState(false);
  const [editExp, setEditExp] = useState(null); // expense being edited
  const [deleteConfirm, setDeleteConfirm] = useState(null); // expense pending delete
  const [form, setForm] = useState({ title: "", amount: "", paidDate: today(), dueDate: today(), paidBy: "pool", recurring: false, appliesTo: [] });

  function addExp() {
    if (!form.title || !form.amount) return;
    const base = { id: uid(), title: form.title, amount: parseFloat(form.amount), recurring: form.recurring };
    const exp = tab === "actual"
      ? { ...base, type: "actual", paidDate: form.paidDate, paidBy: form.paidBy, appliesTo: form.appliesTo || [] }
      : { ...base, type: "planned", dueDate: form.dueDate };
    dispatch({ type: "ADD_EXP", exp });
    setForm({ title: "", amount: "", paidDate: today(), dueDate: today(), paidBy: "pool", recurring: false, appliesTo: [] });
    setAddOpen(false);
  }

  function saveEdit() {
    if (!editExp) return;
    dispatch({ type: "UPD_EXP", id: editExp.id, changes: { paidBy: editExp.paidBy, paidDate: editExp.paidDate, title: editExp.title, amount: parseFloat(editExp.amount), appliesTo: editExp.appliesTo || [] } });
    setEditExp(null);
  }

  const actual = state.expenses.filter(e => e.type === "actual");
  const planned = state.expenses.filter(e => e.type === "planned").sort((a, b) => (a.dueDate || "") > (b.dueDate || "") ? 1 : -1);

  return (
    <div>
      <div style={{ display: "flex", background: "#f0f0f0", borderRadius: 10, padding: 4, marginBottom: 16, gap: 4 }}>
        {[["actual", "Actual (Spent)"], ["planned", "Planned (Upcoming)"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} style={{ flex: 1, padding: "8px", borderRadius: 8, border: "none", fontWeight: 600, fontSize: 13, cursor: "pointer", background: tab === v ? "#fff" : "transparent", color: tab === v ? COLORS.blue : COLORS.textMuted, boxShadow: tab === v ? "0 1px 4px rgba(0,0,0,0.1)" : "none", fontFamily: "inherit" }}>
            {l}
          </button>
        ))}
      </div>

      {tab === "actual" && (
        <div>
          {actual.length === 0 && <div style={{ color: COLORS.textMuted, textAlign: "center", padding: "20px 0" }}>No expenses yet</div>}
          {actual.map(e => {
            const m = e.paidBy !== "pool" ? state.members.find(x => x.id === e.paidBy) : null;
            const appliesToNames = e.appliesTo && e.appliesTo.length > 0
              ? e.appliesTo.map(id => state.members.find(x => x.id === id)?.name).filter(Boolean)
              : null;
            const activeCount = state.members.filter(x => x.active).length;
            const splitCount = appliesToNames ? appliesToNames.length : activeCount;
            const perPerson = splitCount > 0 ? e.amount / splitCount : 0;
            return (
              <div key={e.id} style={{ ...S.card, margin: "0 0 8px", padding: "12px 14px" }}>
                {/* Line 1: title + amount + actions */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", gap: 6 }}>
                      {e.title}
                      {e.recurring && <Icon name="repeat" size={13} color={COLORS.textMuted} />}
                    </div>
                  </div>
                  <span style={{ fontWeight: 800, fontSize: 16, color: COLORS.text, whiteSpace: "nowrap" }}>{bwp(e.amount)}</span>
                  <button onClick={() => setEditExp({ ...e })} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.blue, padding: 4 }}><Icon name="edit" size={16} /></button>
                  <button onClick={() => setDeleteConfirm(e)} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.red, padding: 4 }}><Icon name="delete" size={16} /></button>
                </div>
                {/* Line 2: date + paid by badge */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: appliesToNames ? 6 : 0 }}>
                  <span style={{ fontSize: 12, color: COLORS.textMuted, flex: 1 }}>Paid {e.paidDate}</span>
                  <Badge label={m ? "Personal" : "Pool"} color={m ? COLORS.purple : COLORS.blue} />
                  {m && <span style={{ fontSize: 12, color: COLORS.textMuted }}>by {m.name}</span>}
                </div>
                {/* Line 3: applies to + per-person split */}
                {appliesToNames && (
                  <div style={{ background: `${COLORS.blue}10`, borderRadius: 8, padding: "5px 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: COLORS.blue }}>Splits: {appliesToNames.join(", ")}</span>
                    <span style={{ fontSize: 11, color: COLORS.blue, fontWeight: 700 }}>{bwp(perPerson)} each</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "planned" && (
        <div>
          {planned.length === 0 && <div style={{ color: COLORS.textMuted, textAlign: "center", padding: "20px 0" }}>No planned expenses</div>}
          {planned.map(e => (
            <div key={e.id} style={{ ...S.card, margin: "0 0 8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.title}</span>
                    {e.recurring && <Icon name="repeat" size={14} color={COLORS.textMuted} />}
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>Due {e.dueDate || "—"} · {e.recurring ? "Recurring" : "One-time"}</div>
                </div>
                <span style={{ fontWeight: 700, color: COLORS.amber, whiteSpace: "nowrap" }}>{bwp(e.amount)}</span>
                <Btn size="sm" variant="success" onClick={() => dispatch({ type: "MARK_PAID", id: e.id, date: today() })}>Paid</Btn>
                <button onClick={() => setDeleteConfirm(e)} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.red, padding: 4 }}><Icon name="delete" size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Btn onClick={() => setAddOpen(true)} style={{ width: "100%", justifyContent: "center", marginTop: 8 }}><Icon name="add" size={16} /> Add Expense</Btn>

      {/* Add Expense modal */}
      <Modal open={addOpen} onClose={() => setAddOpen(false)} title={`Add ${tab === "actual" ? "Actual" : "Planned"} Expense`}>
        <Field label="Title">
          <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Electricity bill" style={S.input} />
        </Field>
        <Field label="Amount (BWP)">
          <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0.00" style={S.input} />
        </Field>
        {tab === "actual" ? (
          <>
            <Field label="Paid Date">
              <input type="date" value={form.paidDate} onChange={e => setForm({ ...form, paidDate: e.target.value })} style={S.input} />
            </Field>
            <Field label="Paid By">
              <select value={form.paidBy} onChange={e => setForm({ ...form, paidBy: e.target.value })} style={S.select}>
                <option value="pool">Pool</option>
                {state.members.filter(m => m.active).map(m => <option key={m.id} value={m.id}>{m.name} (personal)</option>)}
              </select>
            </Field>
            <Field label="Applies To">
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>Untick to exclude a member from sharing this expense</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {state.members.filter(m => m.active).map(m => {
                  const allIds = state.members.filter(x => x.active).map(x => x.id);
                  const current = form.appliesTo && form.appliesTo.length > 0 ? form.appliesTo : allIds;
                  const checked = current.includes(m.id);
                  return (
                    <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 10, background: checked ? `${COLORS.blue}10` : COLORS.bg, border: `1.5px solid ${checked ? COLORS.blue : "#e0e0e0"}`, cursor: "pointer" }}>
                      <input type="checkbox" checked={checked} onChange={ev => {
                        const next = ev.target.checked ? [...current, m.id] : current.filter(id => id !== m.id);
                        setForm({ ...form, appliesTo: next.length === allIds.length ? [] : next });
                      }} style={{ width: 16, height: 16, accentColor: COLORS.blue }} />
                      <span style={{ fontWeight: checked ? 600 : 400, fontSize: 14, color: checked ? COLORS.text : COLORS.textMuted }}>{m.name}</span>
                      {!checked && <span style={{ marginLeft: "auto", fontSize: 11, color: COLORS.red, fontWeight: 600 }}>Excluded</span>}
                    </label>
                  );
                })}
              </div>
            </Field>
          </>
        ) : (
          <>
            <Field label="Due Date">
              <input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} style={S.input} />
            </Field>
            <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 16, cursor: "pointer" }}>
              <input type="checkbox" checked={form.recurring} onChange={e => setForm({ ...form, recurring: e.target.checked })} />
              <span style={{ fontSize: 14 }}>Recurring expense</span>
            </label>
          </>
        )}
        <Btn onClick={addExp} style={{ width: "100%", justifyContent: "center" }} disabled={!form.title || !form.amount}>Add Expense</Btn>
      </Modal>

      {/* Edit Expense modal */}
      <Modal open={!!editExp} onClose={() => setEditExp(null)} title="Edit Expense">
        {editExp && (
          <>
            <Field label="Title">
              <input value={editExp.title} onChange={e => setEditExp({ ...editExp, title: e.target.value })} style={S.input} />
            </Field>
            <Field label="Amount (BWP)">
              <input type="number" value={editExp.amount} onChange={e => setEditExp({ ...editExp, amount: e.target.value })} style={S.input} />
            </Field>
            <Field label="Paid Date">
              <input type="date" value={editExp.paidDate || ""} onChange={e => setEditExp({ ...editExp, paidDate: e.target.value })} style={S.input} />
            </Field>
            <Field label="Paid By">
              <select value={editExp.paidBy} onChange={e => setEditExp({ ...editExp, paidBy: e.target.value })} style={S.select}>
                <option value="pool">Pool</option>
                {state.members.filter(m => m.active).map(m => <option key={m.id} value={m.id}>{m.name} (personal)</option>)}
              </select>
            </Field>
            <Field label="Applies To">
              <div style={{ fontSize: 11, color: COLORS.textMuted, marginBottom: 8 }}>Untick to exclude a member from sharing this expense</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {state.members.filter(m => m.active).map(m => {
                  const allIds = state.members.filter(x => x.active).map(x => x.id);
                  const current = editExp.appliesTo && editExp.appliesTo.length > 0 ? editExp.appliesTo : allIds;
                  const checked = current.includes(m.id);
                  return (
                    <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 10, background: checked ? `${COLORS.blue}10` : COLORS.bg, border: `1.5px solid ${checked ? COLORS.blue : "#e0e0e0"}`, cursor: "pointer" }}>
                      <input type="checkbox" checked={checked} onChange={ev => {
                        const next = ev.target.checked ? [...current, m.id] : current.filter(id => id !== m.id);
                        setEditExp({ ...editExp, appliesTo: next.length === allIds.length ? [] : next });
                      }} style={{ width: 16, height: 16, accentColor: COLORS.blue }} />
                      <span style={{ fontWeight: checked ? 600 : 400, fontSize: 14, color: checked ? COLORS.text : COLORS.textMuted }}>{m.name}</span>
                      {!checked && <span style={{ marginLeft: "auto", fontSize: 11, color: COLORS.red, fontWeight: 600 }}>Excluded</span>}
                    </label>
                  );
                })}
              </div>
            </Field>
            <Btn onClick={saveEdit} style={{ width: "100%", justifyContent: "center" }}>Save Changes</Btn>
          </>
        )}
      </Modal>

      {/* Delete confirm modal (Issue 5) */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Delete Expense?">
        {deleteConfirm && (
          <>
            <div style={{ fontSize: 15, marginBottom: 6 }}>Are you sure you want to delete:</div>
            <div style={{ ...S.card, background: `${COLORS.red}08`, border: `1px solid ${COLORS.red}33` }}>
              <div style={{ fontWeight: 700 }}>{deleteConfirm.title}</div>
              <div style={{ fontSize: 13, color: COLORS.textMuted }}>{bwp(deleteConfirm.amount)} · {deleteConfirm.paidDate || deleteConfirm.dueDate}</div>
            </div>
            <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 16 }}>This cannot be undone.</div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="outline" onClick={() => setDeleteConfirm(null)} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
              <Btn variant="danger" onClick={() => { dispatch({ type: "DEL_EXP", id: deleteConfirm.id }); setDeleteConfirm(null); }} style={{ flex: 1, justifyContent: "center" }}>Delete</Btn>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

// ─── SCREEN 5: SETTLEMENTS ────────────────────────────────────────────────────
function Settle({ state, dispatch, comp }) {
  const { memberBalances, pool, grossPool } = comp;
  const transferLogs = state.transferLogs || [];
  const pendingSettlements = state.pendingSettlements || [];

  const [reverseConfirm, setReverseConfirm] = useState(null);

  const excludedNames = new Set(state.excludedFromSettle || []);

  function toggleExclude(name) {
    dispatch({ type: "TOGGLE_SETTLE_EXCLUDE", name });
  }

  // ── Build settlement transactions ────────────────────────────────────────────
  // Excluded members are simply skipped this round — their balance is deferred.
  // Included members settle their own balances unchanged. No redistribution.
  const includedMembers = memberBalances.filter(m => !excludedNames.has(m.name));
  const adjustedBalances = memberBalances.map(m => ({
    ...m,
    adjBalance: m.balance,
    isExcluded: excludedNames.has(m.name),
  }));

  const payers = [
    ...adjustedBalances.filter(m => !m.isExcluded && m.adjBalance < 0).map(m => ({ name: m.name, rem: -m.adjBalance, isPool: false })),
    ...(grossPool > 0 ? [{ name: "Pool", rem: grossPool, isPool: true }] : []),
  ].sort((a, b) => b.rem - a.rem);

  const receivers = [
    ...adjustedBalances.filter(m => !m.isExcluded && m.adjBalance > 0).map(m => ({ name: m.name, rem: m.adjBalance, isPool: false })),
    ...(grossPool < 0 ? [{ name: "Pool", rem: -grossPool, isPool: true }] : []),
  ].sort((a, b) => b.rem - a.rem);

  const settlements = [];
  const payersCopy = payers.map(p => ({ ...p }));
  const receiversCopy = receivers.map(r => ({ ...r }));
  let pi = 0, ri = 0;
  while (pi < payersCopy.length && ri < receiversCopy.length) {
    const amt = Math.min(payersCopy[pi].rem, receiversCopy[ri].rem);
    if (amt > 0.005) {
      const key = `${payersCopy[pi].name}|${receiversCopy[ri].name}`;
      const doneLog = transferLogs.find(t => t.from === payersCopy[pi].name && t.to === receiversCopy[ri].name) || null;
      settlements.push({
        from: payersCopy[pi].name, to: receiversCopy[ri].name, amount: amt,
        fromPool: payersCopy[pi].isPool, toPool: receiversCopy[ri].isPool,
        key, done: !!doneLog, doneLog,
      });
    }
    payersCopy[pi].rem -= amt;
    receiversCopy[ri].rem -= amt;
    if (payersCopy[pi].rem < 0.005) pi++;
    if (receiversCopy[ri].rem < 0.005) ri++;
  }

  const allDone = settlements.length > 0 && settlements.every(s => s.done);

  function settle(s) {
    dispatch({ type: "SETTLE_TXN", key: s.key, from: s.from, to: s.to, amount: s.amount, fromPool: s.fromPool, toPool: s.toPool });
  }

  function doReverse(logEntry) {
    dispatch({ type: "REVERSE_SETTLEMENT", logId: logEntry.id });
    setReverseConfirm(null);
  }

  return (
    <div>
      {/* ── Exclusion panel ─────────────────────────────────────────────────── */}
      <div style={{ ...S.card, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: COLORS.textMuted }}>Settlement Participants</div>
          {excludedNames.size > 0 && (
            <button onClick={() => dispatch({ type: "CLEAR_SETTLE_EXCLUDES" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: COLORS.blue, fontFamily: "inherit", fontWeight: 600 }}>Reset</button>
          )}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {memberBalances.map(m => {
            const excluded = excludedNames.has(m.name);
            return (
              <button key={m.id} onClick={() => toggleExclude(m.name)} style={{
                display: "flex", alignItems: "center", gap: 6, padding: "6px 12px",
                borderRadius: 20, border: `1.5px solid ${excluded ? COLORS.red : COLORS.green}`,
                background: excluded ? `${COLORS.red}12` : `${COLORS.green}12`,
                cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
              }}>
                <Avatar name={m.name} size={20} bg={excluded ? COLORS.red : COLORS.green} />
                <span style={{ fontSize: 13, fontWeight: 600, color: excluded ? COLORS.red : COLORS.text, textDecoration: excluded ? "line-through" : "none" }}>{m.name}</span>
                {excluded && <span style={{ fontSize: 10, color: COLORS.red, fontWeight: 700 }}>OUT</span>}
              </button>
            );
          })}
        </div>
        {excludedNames.size > 0 && (
          <div style={{ marginTop: 10, background: `${COLORS.amber}15`, border: `1px solid ${COLORS.amber}44`, borderRadius: 10, padding: "8px 12px", fontSize: 12, color: COLORS.amber }}>
            {[...excludedNames].join(", ")} excluded · their share redistributed proportionally among remaining members
          </div>
        )}
      </div>

      {/* ── Carried-forward pending settlements ─────────────────────────────── */}
      {pendingSettlements.length > 0 && (
        <div style={{ ...S.card, border: `1.5px solid ${COLORS.amber}66`, marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.amber, marginBottom: 8 }}>⚠ Carried Over — Unsettled from last period</div>
          {pendingSettlements.map((s, i) => (
            <div key={s.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < pendingSettlements.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600, color: s.fromPool ? COLORS.cyan : COLORS.red, fontSize: 13 }}>{s.from}</span>
                <span style={{ color: COLORS.textMuted, fontSize: 12 }}>→</span>
                <span style={{ fontWeight: 600, color: COLORS.green, fontSize: 13 }}>{s.to}</span>
                <span style={{ fontSize: 12, color: COLORS.textMuted }}>{bwp(s.amount)}</span>
              </div>
              <Btn size="sm" variant="danger" onClick={() => dispatch({ type: "CLEAR_SETTLEMENT", key: s.key })}>Clear</Btn>
            </div>
          ))}
        </div>
      )}

      {/* ── Current period: Who Pays Who ─────────────────────────────────────── */}
      {settlements.length > 0 ? (
        <div style={{ ...S.card, background: COLORS.navy, border: "none", marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", color: "#aaa" }}>Who Pays Who</div>
            {allDone && <div style={{ background: `${COLORS.green}33`, borderRadius: 20, padding: "3px 10px" }}><span style={{ fontSize: 11, fontWeight: 700, color: "#69f0ae" }}>✓ All settled</span></div>}
          </div>
          {settlements.map((s, i) => (
            <div key={s.key} style={{ padding: "10px 0", borderBottom: i < settlements.length - 1 ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ background: s.fromPool ? `${COLORS.cyan}33` : `${COLORS.red}33`, borderRadius: 20, padding: "3px 10px" }}>
                    <span style={{ fontWeight: 700, color: s.fromPool ? COLORS.cyan : "#ff8a80", fontSize: 13 }}>{s.from}</span>
                  </div>
                  <span style={{ color: "#aaa", fontSize: 13 }}>→</span>
                  <div style={{ background: `${COLORS.green}33`, borderRadius: 20, padding: "3px 10px" }}>
                    <span style={{ fontWeight: 700, color: "#69f0ae", fontSize: 13 }}>{s.to}</span>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 800, color: s.done ? "#555" : "#fff", fontSize: 15, textDecoration: s.done ? "line-through" : "none" }}>{bwp(s.amount)}</span>
                  {!s.done ? (
                    <button onClick={() => settle(s)} style={{ background: `${COLORS.green}33`, border: `1px solid ${COLORS.green}55`, borderRadius: 8, cursor: "pointer", padding: "5px 10px", color: "#69f0ae", fontSize: 12, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap" }}>
                      Settle ✓
                    </button>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 11, color: "#69f0ae" }}>✓ Settled</span>
                      <button onClick={() => setReverseConfirm(s.doneLog)} style={{ background: "rgba(229,57,53,0.25)", border: "1px solid rgba(229,57,53,0.4)", borderRadius: 8, cursor: "pointer", padding: "4px 8px", color: "#ff8a80", fontSize: 11, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap" }}>
                        Reverse
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {!s.done && (
                <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
                  {s.fromPool
                    ? `Pool pays out ${bwp(s.amount)} to ${s.to} — reduces pool balance`
                    : `${s.from} pays ${s.to} directly — logged as transfer`}
                </div>
              )}
            </div>
          ))}
          {pool !== 0 && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.08)", fontSize: 11, color: "#777" }}>
              Pool {bwp(Math.abs(pool))} {pool > 0 ? "surplus" : "deficit"} included above
            </div>
          )}
        </div>
      ) : (
        <div style={{ ...S.card, textAlign: "center", color: COLORS.textMuted, padding: "18px 16px", marginBottom: 14 }}>
          <Icon name="check" size={28} color={COLORS.green} />
          <div style={{ fontWeight: 600, marginTop: 6 }}>All settled</div>
          <div style={{ fontSize: 12, marginTop: 2 }}>No payments needed</div>
        </div>
      )}

      {/* ── Settlement Log ───────────────────────────────────────────────────── */}
      {transferLogs.length > 0 && (
        <div style={S.card}>
          <div style={S.label}>Settlement Log</div>
          {transferLogs.map((t, i) => (
            <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < transferLogs.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  <span style={{ color: t.fromPool ? COLORS.cyan : COLORS.red }}>{t.from}</span>
                  <span style={{ color: COLORS.textMuted }}> → </span>
                  <span style={{ color: COLORS.green }}>{t.to}</span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted }}>{t.date} · {t.fromPool ? "Pool payout" : "Member transfer"}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <span style={{ fontWeight: 700, color: COLORS.text }}>{bwp(t.amount)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Per-member breakdown ─────────────────────────────────────────────── */}
      <div style={S.label}>Member Breakdown</div>
      {adjustedBalances.map(m => {
        const isExcluded = m.isExcluded;
        return (
          <div key={m.id} style={{ ...S.card, margin: "0 0 10px", opacity: isExcluded ? 0.5 : 1, border: isExcluded ? `1.5px dashed ${COLORS.red}55` : `1px solid ${COLORS.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <Avatar name={m.name} size={36} bg={isExcluded ? "#bbb" : m.balance >= 0 ? COLORS.green : COLORS.red} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                  {m.name}
                  {isExcluded && <span style={{ fontSize: 10, background: `${COLORS.red}20`, color: COLORS.red, borderRadius: 10, padding: "1px 7px", fontWeight: 700 }}>EXCLUDED</span>}
                </div>
                <div style={{ fontSize: 11, color: COLORS.textMuted }}>
                  {isExcluded ? "Excluded from this settlement" : m.balance >= 0 ? "Pool owes them" : "Owes the pool"}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: isExcluded ? "#bbb" : m.adjBalance >= 0 ? COLORS.green : COLORS.red }}>{bwp(Math.abs(isExcluded ? m.balance : m.adjBalance))}</div>
                <div style={{ fontSize: 10, color: COLORS.textMuted }}>{isExcluded ? "Excluded (original balance)" : "Effective balance"}</div>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              <div style={{ background: COLORS.bg, borderRadius: 10, padding: "8px 10px" }}>
                <div style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 600, marginBottom: 6 }}>CONTRIBUTED</div>
                {[["Cash", m.cash, COLORS.green], ["Personal", m.personal, COLORS.purple], ["Credit", m.credit, COLORS.blue], ["OB Share", m.obCredit, COLORS.cyan]].map(([l, v, c]) => (
                  <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>{l}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: v > 0 ? c : "#ccc" }}>{bwp(v)}</span>
                  </div>
                ))}
                <div style={{ borderTop: `1px solid ${COLORS.border}`, marginTop: 4, paddingTop: 4, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 11, fontWeight: 700 }}>Total</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: COLORS.navy }}>{bwp(m.totalIn)}</span>
                </div>
              </div>
              <div style={{ background: COLORS.bg, borderRadius: 10, padding: "8px 10px" }}>
                <div style={{ fontSize: 10, color: COLORS.textMuted, fontWeight: 600, marginBottom: 6 }}>THEIR SHARE</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.red, marginBottom: 4 }}>{bwp(m.share)}</div>
                <div style={{ fontSize: 10, color: COLORS.textMuted }}>Based on applicable expenses</div>
                <div style={{ marginTop: 8, borderTop: `1px solid ${COLORS.border}`, paddingTop: 6 }}>
                  <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 2 }}>{isExcluded ? "Original Balance" : "Effective Balance"}</div>
                  <div style={{ fontSize: 14, fontWeight: 800 }}>
                    <Amt n={isExcluded ? m.balance : m.adjBalance} color={(isExcluded ? m.balance : m.adjBalance) >= 0 ? COLORS.green : COLORS.red} bold showSign size={14} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* ── Reverse confirmation modal ───────────────────────────────────────── */}
      <Modal open={!!reverseConfirm} onClose={() => setReverseConfirm(null)} title="Reverse Settlement">
        {reverseConfirm && (
          <div>
            <div style={{ background: `${COLORS.red}12`, border: `1px solid ${COLORS.red}33`, borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                <span style={{ color: reverseConfirm.fromPool ? COLORS.cyan : COLORS.red }}>{reverseConfirm.from}</span>
                <span style={{ color: COLORS.textMuted }}> → </span>
                <span style={{ color: COLORS.green }}>{reverseConfirm.to}</span>
                <span style={{ color: COLORS.text }}> · {bwp(reverseConfirm.amount)}</span>
              </div>
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>Settled on {reverseConfirm.date}</div>
            </div>
            <div style={{ fontSize: 13, color: COLORS.text, marginBottom: 6 }}>This will:</div>
            <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>• Remove this entry from the settlement log</div>
            {reverseConfirm.fromPool && (
              <div style={{ fontSize: 13, color: "#555", marginBottom: 6 }}>• Restore <strong>{bwp(reverseConfirm.amount)}</strong> back to the pool balance</div>
            )}
            <div style={{ fontSize: 13, color: "#555", marginBottom: 18 }}>• The transaction will reappear as unsettled</div>
            <div style={{ display: "flex", gap: 10 }}>
              <Btn variant="outline" onClick={() => setReverseConfirm(null)} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
              <Btn variant="danger" onClick={() => doReverse(reverseConfirm)} style={{ flex: 1, justifyContent: "center" }}>Reverse It</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── SCREEN 6: REPORTS ────────────────────────────────────────────────────────
function Reports({ state, comp }) {
  const { cashPaid, personalTotal, actualTotal, plannedTotal, pool, projected, memberBalances } = comp;
  const settledPayouts = (state.transferLogs || []).filter(t => t.fromPool).reduce((s, t) => s + t.amount, 0);

  // Adjusted balances — excluded members are skipped, no redistribution
  const excludedNames = new Set(state.excludedFromSettle || []);
  const adjustedMemberBalances = memberBalances.map(m => ({
    ...m,
    adjBalance: m.balance,
    isExcluded: excludedNames.has(m.name),
  }));

  const [reportModal, setReportModal] = useState(null); // { title, content, filename }

  // ── Report formatting ─────────────────────────────────────────────────────
  // All columns designed to fit within 56 chars total width
  const W = 56;
  function fmtN(n) {
    return Math.abs(parseFloat(n) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }
  // Amount: "BWP " (4) + 12-char right-aligned number = 16 chars total, always same width
  function fmtAmt(n) { return "BWP " + fmtN(n).padStart(12); }
  // Balance: same 16 chars, sign sits inside the 12-char number field so BWP never shifts
  function fmtBalance(n) {
    const num = parseFloat(n) || 0;
    return "BWP " + ((num >= 0 ? "+" : "-") + fmtN(num)).padStart(12);
  }
  function rRow(label, value, lw = 20) { return "  " + label.padEnd(lw) + " " + value + "\n"; }
  function rDiv(ch) { return (ch || "-").repeat(W) + "\n"; }
  function rSec(title) { return "\n" + title + "\n" + rDiv() ; }

  function buildReport(label, ob, tc, tp, ta, pl, po, members, contributions, expenses, logs, sourceMembers) {
    let t = "";
    // Header — plain text, fits W=56
    t += "=".repeat(W) + "\n";
    t += ("  HOUSETAB REPORT").padEnd(W) + "\n";
    t += "=".repeat(W) + "\n";
    t += "  Period  : " + label + "\n";
    t += "  Printed : " + today() + "\n";
    t += rDiv();

    // Financial Summary — label 20, amount 16 = 38 chars + 2 indent = 40
    t += rSec("FINANCIAL SUMMARY");
    t += rRow("Opening Balance",    fmtAmt(ob));
    t += rRow("Cash Contributions", fmtAmt(tc));
    if ((tp || 0) > 0) t += rRow("Personal Payments",  fmtAmt(tp));
    t += rRow("Actual Expenses",    fmtAmt(ta));
    const sp = (logs || []).filter(x => x.fromPool).reduce((s, x) => s + x.amount, 0);
    if (sp > 0) t += rRow("Settled Payouts", fmtAmt(sp));
    t += rDiv(".");
    t += rRow("Pool Balance",       fmtAmt(po));
    if ((pl || 0) > 0) {
      t += rRow("Planned Expenses", fmtAmt(pl));
      t += rDiv(".");
      t += rRow("Projected Balance", fmtAmt(po - pl));
    }

    // Member Breakdown
    t += rSec("MEMBER BREAKDOWN");
    (members || []).forEach(m => {
      t += "\n  " + m.name + (m.isExcluded ? " [EXCLUDED]" : "") + "\n";
      t += rRow("  Cash Paid",     fmtAmt(m.cash));
      if ((m.personal || 0) > 0) t += rRow("  Personal Paid", fmtAmt(m.personal));
      if ((m.credit   || 0) > 0) t += rRow("  Credit",        fmtAmt(m.credit));
      if (!m.isExcluded && (m.obCredit || 0) > 0) t += rRow("  OB Share", fmtAmt(m.obCredit));
      t += rRow("  Total In",      fmtAmt(m.totalIn));
      t += rRow("  Expense Share", fmtAmt(m.share));
      const bal = m.isExcluded ? m.balance : (m.adjBalance !== undefined ? m.adjBalance : m.balance);
      t += rRow("  Balance",       fmtBalance(bal));
    });

    // Contributions — date 10 + name 12 + amount 16 + status = 42 + 2 indent = 44
    if ((contributions || []).length > 0) {
      t += rSec("CONTRIBUTIONS");
      t += "  " + "DATE".padEnd(10) + "  " + "MEMBER".padEnd(12) + "  " + "AMOUNT".padStart(16) + "\n";
      t += rDiv(".");
      contributions.forEach(c => {
        const mn = (sourceMembers || []).find(x => x.id === c.memberId);
        t += "  " + (c.date || "").padEnd(10) + "  " + (mn?.name || "?").substring(0, 12).padEnd(12) + "  " + fmtAmt(c.amount) + "\n";
      });
    }

    // Expenses — date 10 + title 14 + amount 16 + payer = 42 + 2 indent = 44
    const actualExp = (expenses || []).filter(e => e.type === "actual");
    if (actualExp.length > 0) {
      t += rSec("ACTUAL EXPENSES");
      t += "  " + "DATE".padEnd(10) + "  " + "DESCRIPTION".padEnd(14) + "  " + "AMOUNT".padStart(16) + "  BY\n";
      t += rDiv(".");
      actualExp.forEach(e => {
        const payer = e.paidBy === "pool" ? "Pool" : ((sourceMembers || []).find(x => x.id === e.paidBy)?.name || "?").substring(0, 8);
        t += "  " + (e.paidDate || "").padEnd(10) + "  " + e.title.substring(0, 14).padEnd(14) + "  " + fmtAmt(e.amount) + "  " + payer + "\n";
      });
    }

    // Settlement Log
    if ((logs || []).length > 0) {
      t += rSec("SETTLEMENT LOG");
      t += "  " + "DATE".padEnd(10) + "  " + "FROM".padEnd(10) + "  " + "TO".padEnd(10) + "  " + "AMOUNT".padStart(16) + "\n";
      t += rDiv(".");
      logs.forEach(tl => {
        t += "  " + tl.date.padEnd(10) + "  " + tl.from.substring(0, 10).padEnd(10) + "  " + tl.to.substring(0, 10).padEnd(10) + "  " + fmtAmt(tl.amount) + "\n";
      });
    }

    t += "\n" + rDiv();
    t += "  Generated by HouseTab\n";
    return t;
  }

  function showReport(txt, title, filename) {
    setReportModal({ title, content: txt, filename });
  }

  function download(content, filename, type) {
    const a = document.createElement("a");
    a.href = "data:" + type + ";charset=utf-8," + encodeURIComponent(content);
    a.download = filename;
    a.click();
  }

  function exportCurrentReport() {
    const txt = buildReport(state.period?.label, state.ob, cashPaid, personalTotal, actualTotal, plannedTotal, pool, memberBalances, state.contributions, state.expenses, state.transferLogs, state.members);
    showReport(txt, `Report — ${state.period?.label}`, `housetab-${state.period?.month || "report"}.txt`);
  }

  function exportArchiveReport(snap) {
    const cashPd = (snap.contributions || []).filter(c => c.status === "Paid").reduce((s, c) => s + c.amount, 0);
    const pers = (snap.expenses || []).filter(e => e.type === "actual" && e.paidBy !== "pool").reduce((s, e) => s + e.amount, 0);
    const actv = (snap.members || []).filter(m => m.active);
    const obCr = actv.length ? snap.ob / actv.length : 0;
    const mbs = actv.map(m => {
      const cash = (snap.contributions || []).filter(c => c.memberId === m.id && c.status === "Paid").reduce((s, c) => s + c.amount, 0);
      const personal = (snap.expenses || []).filter(e => e.paidBy === m.id && e.type === "actual").reduce((s, e) => s + e.amount, 0);
      const share = actv.length ? snap.ta / actv.length : 0;
      return { ...m, cash, personal, credit: 0, obCredit: obCr, totalIn: cash + personal + obCr, share, balance: cash + personal + obCr - share };
    });
    const txt = buildReport(snap.label, snap.ob, cashPd, pers, snap.ta, 0, snap.pool, mbs, snap.contributions, snap.expenses, snap.transferLogs || [], snap.members);
    showReport(txt, `Report — ${snap.label}`, `housetab-${snap.month || snap.label}.txt`);
  }

  const maxContrib = Math.max(...memberBalances.map(m => m.totalIn), 1);

  return (
    <div>
      {/* Financial Summary */}
      <div style={S.card}>
        <span style={S.label}>Financial Summary — {state.period?.label}</span>
        {[["+ Opening Balance", state.ob, COLORS.cyan], ["+ Cash Contributions", cashPaid, COLORS.blue], ["+ Personal Payments", personalTotal, COLORS.purple], ["− Actual Expenses", actualTotal, COLORS.red]].map(([l, v, c]) => (
          <div key={l} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 13, color: "#555", flex: 1 }}>{l}</span>
            <Amt n={v} color={c} bold />
          </div>
        ))}
        {settledPayouts > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${COLORS.border}` }}>
            <span style={{ fontSize: 13, color: "#555", flex: 1 }}>− Settled Payouts</span>
            <Amt n={settledPayouts} color={COLORS.green} bold />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
          <span style={{ fontWeight: 800, flex: 1 }}>= Pool Balance</span>
          <Amt n={pool} color={pool >= 0 ? COLORS.green : COLORS.red} bold size={15} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderTop: `1px solid ${COLORS.border}` }}>
          <span style={{ fontSize: 13, color: "#555", flex: 1 }}>− Planned Expenses</span>
          <Amt n={plannedTotal} color={COLORS.amber} bold />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0" }}>
          <span style={{ fontWeight: 800, flex: 1 }}>= Projected Balance</span>
          <Amt n={projected} color={projected >= 0 ? COLORS.green : COLORS.amber} bold size={15} />
        </div>
      </div>

      {/* Member Balances */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={S.label}>Member Balances</span>
          {excludedNames.size > 0 && <span style={{ fontSize: 11, color: COLORS.amber, fontWeight: 600 }}>Settle exclusions active</span>}
        </div>
        {adjustedMemberBalances.map(m => {
          const displayBalance = m.isExcluded ? m.balance : m.adjBalance;
          return (
            <div key={m.id} style={{ padding: "8px 0", borderBottom: `1px solid ${COLORS.border}`, opacity: m.isExcluded ? 0.45 : 1 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Avatar name={m.name} size={28} bg={m.isExcluded ? "#bbb" : displayBalance >= 0 ? COLORS.green : COLORS.red} />
                  <div>
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{m.name}</span>
                    {m.isExcluded && <span style={{ marginLeft: 6, fontSize: 10, color: COLORS.red, background: `${COLORS.red}18`, borderRadius: 8, padding: "1px 6px" }}>EXCLUDED</span>}
                  </div>
                </div>
                <span style={{ fontWeight: 700, color: m.isExcluded ? "#bbb" : displayBalance >= 0 ? COLORS.green : COLORS.red }}>
                  <Amt n={displayBalance} color={m.isExcluded ? "#bbb" : displayBalance >= 0 ? COLORS.green : COLORS.red} bold showSign size={14} />
                </span>
              </div>
              <div style={{ display: "flex", gap: 10, paddingLeft: 36, flexWrap: "wrap" }}>
                {[["Cash", m.cash, COLORS.blue], ["Personal", m.personal, COLORS.purple], ["Share", m.share, COLORS.red]].map(([l, v, c]) => (
                  <span key={l} style={{ fontSize: 11, color: COLORS.textMuted }}>{l}: <strong style={{ color: c }}>{bwp(v)}</strong></span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Contributions chart */}
      <div style={S.card}>
        <div style={S.label}>Contributions</div>
        {memberBalances.map(m => (
          <div key={m.id} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
              <span style={{ fontWeight: 500 }}>{m.name}</span>
              <span>{bwp(m.totalIn)}</span>
            </div>
            <div style={{ background: "#f0f0f0", borderRadius: 6, height: 8, overflow: "hidden" }}>
              <div style={{ width: `${(m.totalIn / maxContrib) * 100}%`, height: "100%", background: `linear-gradient(90deg, ${COLORS.blue}, ${COLORS.cyan})`, borderRadius: 6, transition: "width 0.5s" }} />
            </div>
          </div>
        ))}
      </div>

      {/* Export Reports */}
      <div style={S.card}>
        <div style={S.label}>Export Reports</div>
        <Btn onClick={exportCurrentReport} variant="ghost" style={{ width: "100%", justifyContent: "center", marginBottom: 10 }}>
          <Icon name="chart" size={15} /> View / Download {state.period?.label}
        </Btn>
        {state.archive.length > 0 && (
          <>
            <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 8 }}>Past periods</div>
            {state.archive.map((snap, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: i < state.archive.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{snap.label}</div>
                  <div style={{ fontSize: 11, color: COLORS.textMuted }}>Closed {snap.closedAt} · Pool {bwp(snap.pool)}</div>
                </div>
                <Btn size="sm" variant="ghost" onClick={() => exportArchiveReport(snap)}>View</Btn>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Report viewer modal */}
      <Modal open={!!reportModal} onClose={() => setReportModal(null)} title={reportModal?.title || "Report"} wide>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 10 }}>Tap anywhere in the text to select all, then copy. Or download as a .txt file.</div>
        <textarea
          readOnly
          value={reportModal?.content || ""}
          onFocus={e => e.target.select()}
          onClick={e => e.target.select()}
          style={{ width: "100%", height: 340, fontFamily: "monospace", fontSize: 11, lineHeight: 1.5, padding: 12, borderRadius: 8, border: `1px solid ${COLORS.border}`, resize: "none", background: "#fafafa", boxSizing: "border-box", color: COLORS.text }}
        />
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <Btn
            variant="ghost"
            onClick={() => { try { navigator.clipboard.writeText(reportModal?.content || ""); } catch(e2) {} }}
            style={{ flex: 1, justifyContent: "center" }}
          >
            Copy Text
          </Btn>
          <Btn
            onClick={() => { download(reportModal.content, reportModal.filename, "text/plain"); setReportModal(null); }}
            style={{ flex: 1, justifyContent: "center" }}
          >
            <Icon name="archive" size={15} /> Download .txt
          </Btn>
        </div>
      </Modal>
    </div>
  );
}

// ─── SCREEN 8: SETTINGS ───────────────────────────────────────────────────────
function Settings({ state, dispatch }) {
  const cfg = state.settings || {};
  const [clearStep, setClearStep] = useState(0);
  const [resetStep, setResetStep] = useState(0);
  const [restoreError, setRestoreError] = useState("");
  const [backupDone, setBackupDone] = useState(false);
  const [reminderModal, setReminderModal] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [rForm, setRForm] = useState({ title: "", dayOfMonth: 1, daysBeforeWarn: 3 });

  function set(key, val) { dispatch({ type: "SET_SETTINGS", settings: { [key]: val } }); }

  function downloadBackup() {
    const a = document.createElement("a");
    a.href = "data:application/json;charset=utf-8," + encodeURIComponent(JSON.stringify(state, null, 2));
    a.download = `housetab-backup-${today()}.json`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setBackupDone(true); setTimeout(() => setBackupDone(false), 3000);
  }

  function restoreFromFile(e) {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!data.setup || !data.members) { setRestoreError("Invalid backup — missing required fields."); return; }
        dispatch({ type: "RESTORE_BACKUP", data }); setRestoreError("");
      } catch(err) { setRestoreError("Could not read file. Make sure it is a HouseTab .json backup."); }
    };
    reader.readAsText(file); e.target.value = "";
  }

  function openAddReminder() { setEditingReminder(null); setRForm({ title: "", dayOfMonth: 1, daysBeforeWarn: 3 }); setReminderModal(true); }
  function openEditReminder(r) { setEditingReminder(r); setRForm({ title: r.title, dayOfMonth: r.dayOfMonth, daysBeforeWarn: r.daysBeforeWarn }); setReminderModal(true); }
  function saveReminder() {
    if (!rForm.title.trim()) return;
    const data = { title: rForm.title.trim(), dayOfMonth: parseInt(rForm.dayOfMonth) || 1, daysBeforeWarn: parseInt(rForm.daysBeforeWarn) || 3 };
    if (editingReminder) { dispatch({ type: "UPD_REMINDER", id: editingReminder.id, changes: data }); }
    else { dispatch({ type: "ADD_REMINDER", reminder: { id: uid(), ...data, active: true } }); }
    setReminderModal(false);
  }

  const Row = ({ label, sub, children }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` }}>
      <div style={{ flex: 1, paddingRight: 12 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
  const Toggle = ({ value, onChange }) => (
    <div onClick={onChange} style={{ width: 44, height: 24, borderRadius: 12, background: value ? COLORS.blue : "#ccc", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
      <div style={{ position: "absolute", top: 3, left: value ? 23 : 3, width: 18, height: 18, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
    </div>
  );

  return (
    <div>
      {/* Display Settings */}
      <div style={S.card}>
        <div style={S.label}>Display</div>
        <Row label="Show Opening Balance on Dashboard" sub="OB hero card on the home screen">
          <Toggle value={cfg.showOBOnDash !== false} onChange={() => set("showOBOnDash", !(cfg.showOBOnDash !== false))} />
        </Row>
        <Row label="Confirm Before Deleting" sub="Ask before removing contributions or expenses">
          <Toggle value={cfg.confirmDeletes !== false} onChange={() => set("confirmDeletes", !(cfg.confirmDeletes !== false))} />
        </Row>
        <Row label="Show Planned Expenses on Dashboard" sub="Upcoming bills widget on home screen">
          <Toggle value={cfg.showPlanned !== false} onChange={() => set("showPlanned", !(cfg.showPlanned !== false))} />
        </Row>
        <Row label="Show Unpaid Members on Dashboard" sub="Highlight members with outstanding contributions">
          <Toggle value={cfg.showUnpaid !== false} onChange={() => set("showUnpaid", !(cfg.showUnpaid !== false))} />
        </Row>
      </div>

      {/* Reminders */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={S.label}>Reminders</span>
          <Btn size="sm" onClick={openAddReminder}><Icon name="add" size={14} /> Add</Btn>
        </div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 10 }}>Reminders appear as banners on the dashboard when due.</div>
        {(state.reminders || []).length === 0 && (
          <div style={{ fontSize: 13, color: COLORS.textMuted, textAlign: "center", padding: "12px 0" }}>No reminders set</div>
        )}
        {(state.reminders || []).map(r => (
          <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${COLORS.border}` }}>
            <div onClick={() => dispatch({ type: "UPD_REMINDER", id: r.id, changes: { active: !r.active } })}
              style={{ width: 38, height: 20, borderRadius: 10, background: r.active !== false ? COLORS.blue : "#ccc", cursor: "pointer", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
              <div style={{ position: "absolute", top: 2, left: r.active !== false ? 20 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s" }} />
            </div>
            <div style={{ flex: 1, opacity: r.active === false ? 0.4 : 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{r.title}</div>
              <div style={{ fontSize: 11, color: COLORS.textMuted }}>Day {r.dayOfMonth} of month · warn {r.daysBeforeWarn} days before</div>
            </div>
            <button onClick={() => openEditReminder(r)} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.blue, padding: 4 }}><Icon name="edit" size={16} /></button>
            <button onClick={() => dispatch({ type: "DEL_REMINDER", id: r.id })} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.red, padding: 4 }}><Icon name="delete" size={16} /></button>
          </div>
        ))}
      </div>

      {/* Household info */}
      <div style={S.card}>
        <div style={S.label}>Household</div>
        <Row label="Current Period" sub={state.period?.label || "—"}><span style={{ fontSize: 12, color: COLORS.textMuted }}>{state.members.filter(m => m.active).length} active members</span></Row>
        <Row label="Cloud Backup" sub="Data synced to Supabase on every change"><span style={{ fontSize: 12, color: COLORS.green, fontWeight: 600 }}>● Active</span></Row>
        <Row label="Admin" sub="Current household manager"><span style={{ fontSize: 13, fontWeight: 600, color: COLORS.blue }}>{state.members.find(m => m.id === state.admin)?.name || "—"}</span></Row>
        <Row label="Archived Periods" sub="Closed months stored"><span style={{ fontSize: 13, fontWeight: 600 }}>{state.archive.length}</span></Row>
        <Row label="Contributions this month" sub="Paid entries"><span style={{ fontSize: 13, fontWeight: 600 }}>{state.contributions.filter(c => c.status === "Paid").length}</span></Row>
        <Row label="Expenses this month" sub="Actual expenses recorded"><span style={{ fontSize: 13, fontWeight: 600 }}>{state.expenses.filter(e => e.type === "actual").length}</span></Row>
      </div>

      {/* Backup & Restore */}
      <div style={S.card}>
        <div style={S.label}>Backup & Restore</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 12 }}>Download a full backup of all your data as a JSON file, or restore from a previous backup.</div>
        <Btn onClick={downloadBackup} variant="ghost" style={{ width: "100%", justifyContent: "center", marginBottom: 6 }}>
          <Icon name="archive" size={15} /> Download Backup (.json)
        </Btn>
        {backupDone && <div style={{ fontSize: 12, color: COLORS.green, textAlign: "center", marginBottom: 8 }}>✓ Backup downloaded</div>}
        <label style={{ display: "block", marginTop: 10, cursor: "pointer" }}>
          <div style={{ border: `1.5px dashed ${COLORS.blue}`, borderRadius: 10, padding: "10px 14px", textAlign: "center", fontSize: 13, color: COLORS.blue, fontWeight: 600 }}>
            Restore from Backup File
          </div>
          <input type="file" accept=".json" onChange={restoreFromFile} style={{ display: "none" }} />
        </label>
        {restoreError && <div style={{ fontSize: 12, color: COLORS.red, marginTop: 6 }}>{restoreError}</div>}
      </div>

      {/* Clear This Month */}
      <div style={{ ...S.card, border: `1.5px solid ${COLORS.amber}44` }}>
        <div style={S.label}>Clear This Month</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 12 }}>Wipes all contributions, expenses and settlements for {state.period?.label}. Members and opening balance are kept.</div>
        {clearStep === 0 && <Btn variant="amber" onClick={() => setClearStep(1)} style={{ width: "100%", justifyContent: "center" }}><Icon name="delete" size={15} /> Clear This Month</Btn>}
        {clearStep === 1 && (
          <div>
            <div style={{ background: `${COLORS.amber}15`, border: `1px solid ${COLORS.amber}44`, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: COLORS.amber, marginBottom: 12 }}>
              Delete all contributions, expenses and settlements for {state.period?.label}?
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="outline" onClick={() => setClearStep(0)} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
              <Btn variant="amber" onClick={() => setClearStep(2)} style={{ flex: 1, justifyContent: "center" }}>Continue</Btn>
            </div>
          </div>
        )}
        {clearStep === 2 && (
          <div>
            <div style={{ background: `${COLORS.red}15`, border: `1px solid ${COLORS.red}44`, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: COLORS.red, marginBottom: 12 }}>
              <strong>Final confirmation.</strong> {state.contributions.length} contributions and {state.expenses.length} expenses will be permanently deleted.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="outline" onClick={() => setClearStep(0)} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
              <Btn variant="danger" onClick={() => { dispatch({ type: "CLEAR_MONTH" }); setClearStep(0); }} style={{ flex: 1, justifyContent: "center" }}>Delete Everything</Btn>
            </div>
          </div>
        )}
      </div>

      {/* Total Reset */}
      <div style={{ ...S.card, border: `1.5px solid ${COLORS.red}44`, marginBottom: 20 }}>
        <div style={S.label}>Total Reset</div>
        <div style={{ fontSize: 12, color: COLORS.textMuted, marginBottom: 12 }}>Wipes all data and returns to setup. Cannot be undone. Download a backup first.</div>
        {resetStep === 0 && <Btn variant="danger" onClick={() => setResetStep(1)} style={{ width: "100%", justifyContent: "center" }}><Icon name="warning" size={15} /> Total Reset</Btn>}
        {resetStep === 1 && (
          <div>
            <div style={{ background: `${COLORS.red}15`, border: `1px solid ${COLORS.red}44`, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: COLORS.red, marginBottom: 12 }}>
              This will permanently delete ALL data — members, contributions, expenses, history and settings.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="outline" onClick={() => setResetStep(0)} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
              <Btn variant="danger" onClick={() => setResetStep(2)} style={{ flex: 1, justifyContent: "center" }}>Yes, delete all</Btn>
            </div>
          </div>
        )}
        {resetStep === 2 && (
          <div>
            <div style={{ background: `${COLORS.red}15`, border: `1px solid ${COLORS.red}44`, borderRadius: 10, padding: "10px 12px", fontSize: 13, color: COLORS.red, marginBottom: 12 }}>
              <strong>Last chance.</strong> Everything will be gone permanently.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="outline" onClick={() => setResetStep(0)} style={{ flex: 1, justifyContent: "center" }}>Cancel</Btn>
              <Btn variant="danger" onClick={() => { dispatch({ type: "TOTAL_RESET" }); setResetStep(0); }} style={{ flex: 1, justifyContent: "center" }}>Wipe Everything</Btn>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Reminder Modal */}
      <Modal open={reminderModal} onClose={() => setReminderModal(false)} title={editingReminder ? "Edit Reminder" : "Add Reminder"}>
        <Field label="Reminder Title">
          <input value={rForm.title} onChange={e => setRForm({ ...rForm, title: e.target.value })} placeholder="e.g. Pay Internet, Pay Rent" style={S.input} />
        </Field>
        <Field label="Day of Month" error={rForm.dayOfMonth < 1 || rForm.dayOfMonth > 31 ? "Enter a day between 1 and 31" : ""}>
          <input type="number" min={1} max={31} value={rForm.dayOfMonth} onChange={e => setRForm({ ...rForm, dayOfMonth: e.target.value })} style={S.input} />
        </Field>
        <Field label="Warn me X days before">
          <input type="number" min={0} max={14} value={rForm.daysBeforeWarn} onChange={e => setRForm({ ...rForm, daysBeforeWarn: e.target.value })} style={S.input} />
          <div style={{ fontSize: 11, color: COLORS.textMuted, marginTop: 4 }}>Banner appears on dashboard this many days early</div>
        </Field>
        <Btn onClick={saveReminder} disabled={!rForm.title.trim()} style={{ width: "100%", justifyContent: "center" }}>
          {editingReminder ? "Save Changes" : "Add Reminder"}
        </Btn>
      </Modal>
    </div>
  );
}
function History({ state }) {
  const [detail, setDetail] = useState(null);
  const [subTab, setSubTab] = useState("summary");

  if (state.archive.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "48px 20px", color: COLORS.textMuted }}>
        <Icon name="archive" size={48} color="#ddd" />
        <div style={{ marginTop: 12, fontSize: 16, fontWeight: 600 }}>No closed periods yet</div>
        <div style={{ fontSize: 13, marginTop: 6 }}>Close a month to see history here.</div>
      </div>
    );
  }

  if (detail) {
    const snap = detail;
    const active = (snap.members || []).filter(m => m.active);
    const actualExps = (snap.expenses || []).filter(e => e.type === "actual");

    // Respect appliesTo for each expense — same logic as live compute()
    const memberExpenseShare = (memberId) => {
      return actualExps.reduce((total, exp) => {
        const applies = !exp.appliesTo || exp.appliesTo.length === 0
          ? active.map(m => m.id)
          : exp.appliesTo;
        if (!applies.includes(memberId)) return total;
        const splitCount = applies.filter(id => active.find(m => m.id === id)).length;
        return total + (splitCount > 0 ? exp.amount / splitCount : 0);
      }, 0);
    };

    // OB credit: use saved excludedFromSettle if present, otherwise split equally
    const snapExcluded = new Set(snap.excludedFromSettle || []);
    const settlingMembers = active.filter(m => !snapExcluded.has(m.name));
    const obCreditFor = (m) => snapExcluded.has(m.name) ? 0 : (settlingMembers.length > 0 ? snap.ob / settlingMembers.length : 0);

    const memberBal = active.map(m => {
      const cash = (snap.contributions || []).filter(c => c.memberId === m.id && c.status === "Paid").reduce((s, c) => s + c.amount, 0);
      const pers = actualExps.filter(e => e.paidBy === m.id).reduce((s, e) => s + e.amount, 0);
      const obCr = obCreditFor(m);
      const share = memberExpenseShare(m.id);
      const totalIn = cash + pers + obCr;
      return { ...m, cash, personal: pers, obCredit: obCr, totalIn, share, balance: totalIn - share };
    });

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.blue, padding: 4 }}><Icon name="back" size={22} /></button>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 17 }}>{snap.label}</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>Closed {snap.closedAt} · {snap.action === "roll" ? "Rolled Over" : "Distributed"}</div>
          </div>
        </div>

        <div style={{ display: "flex", background: "#f0f0f0", borderRadius: 10, padding: 4, marginBottom: 16, gap: 4, overflowX: "auto" }}>
          {[["summary", "Summary"], ["contributions", "Contributions"], ["expenses", "Expenses"], ["balances", "Balances"]].map(([v, l]) => (
            <button key={v} onClick={() => setSubTab(v)} style={{ flex: 1, minWidth: 70, padding: "7px 4px", borderRadius: 8, border: "none", fontWeight: 600, fontSize: 12, cursor: "pointer", background: subTab === v ? "#fff" : "transparent", color: subTab === v ? COLORS.blue : COLORS.textMuted, fontFamily: "inherit", whiteSpace: "nowrap" }}>{l}</button>
          ))}
        </div>

        {subTab === "summary" && (
          <div>
            <div style={{ background: COLORS.navy, borderRadius: 16, padding: "18px 16px", marginBottom: 12, color: "#fff" }}>
              {[["Opening Balance", bwp(snap.ob), COLORS.cyan], ["Contributions", bwp(snap.tc), COLORS.blue], ["Expenses", bwp(snap.ta), COLORS.red], ["Pool Balance", bwp(snap.pool), snap.pool >= 0 ? COLORS.green : COLORS.red]].map(([l, v, c]) => (
                <div key={l} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 14 }}>
                  <span style={{ color: "#aaa" }}>{l}</span>
                  <span style={{ fontWeight: 700, color: c }}>{v}</span>
                </div>
              ))}
            </div>
            <div style={S.card}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Closing Action</div>
              <div style={{ fontSize: 14, color: COLORS.textMuted }}>{snap.action === "roll" ? `Rolled Over · New OB: ${bwp(snap.pool)}` : `Distributed · ${bwp(snap.pool / (active.length || 1))} per member`}</div>
              {snap.carryPersonal && <div style={{ fontSize: 13, color: COLORS.blue, marginTop: 4 }}>Personal carry: {snap.carryPersonal === "deduct" ? "Deducted next month" : "Let go"}</div>}
            </div>
          </div>
        )}

        {subTab === "contributions" && (
          <div>
            {(snap.contributions || []).length === 0 ? <div style={{ color: COLORS.textMuted, textAlign: "center", padding: 20 }}>No contributions</div> :
              (snap.contributions || []).map((c, i) => {
                const m = (snap.members || []).find(x => x.id === c.memberId);
                return (
                  <div key={i} style={{ ...S.card, display: "flex", justifyContent: "space-between", margin: "0 0 8px" }}>
                    <div><div style={{ fontWeight: 600 }}>{m?.name}</div><div style={{ fontSize: 12, color: COLORS.textMuted }}>{c.date}</div></div>
                    <div style={{ textAlign: "right" }}><div style={{ fontWeight: 700 }}>{bwp(c.amount)}</div><Badge label={c.status} /></div>
                  </div>
                );
              })
            }
          </div>
        )}

        {subTab === "expenses" && (
          <div>
            {(snap.expenses || []).length === 0 ? <div style={{ color: COLORS.textMuted, textAlign: "center", padding: 20 }}>No expenses</div> :
              (snap.expenses || []).map((e, i) => (
                <div key={i} style={{ ...S.card, display: "flex", justifyContent: "space-between", margin: "0 0 8px" }}>
                  <div><div style={{ fontWeight: 600 }}>{e.title}</div><div style={{ fontSize: 12, color: COLORS.textMuted }}>{e.type === "actual" ? `Paid ${e.paidDate}` : `Planned, due ${e.dueDate}`}</div></div>
                  <div style={{ textAlign: "right" }}><div style={{ fontWeight: 700 }}>{bwp(e.amount)}</div><Badge label={e.type === "actual" ? "Actual" : "Planned"} color={e.type === "actual" ? COLORS.blue : COLORS.amber} /></div>
                </div>
              ))
            }
          </div>
        )}

        {subTab === "balances" && (
          <div>
            {memberBal.map(m => (
              <div key={m.id} style={{ ...S.card, margin: "0 0 8px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <Avatar name={m.name} size={32} bg={m.balance >= 0 ? COLORS.green : COLORS.red} />
                  <div style={{ flex: 1, fontWeight: 600 }}>{m.name}</div>
                  <Amt n={m.balance} color={m.balance >= 0 ? COLORS.green : COLORS.red} bold showSign size={14} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 4 }}>
                  {[["Cash", m.cash], ["Personal", m.personal], ["OB Share", m.obCredit], ["Total In", m.totalIn]].map(([l, v]) => (
                    <div key={l} style={{ textAlign: "center", background: COLORS.bg, borderRadius: 8, padding: "5px 3px" }}>
                      <div style={{ fontSize: 10, color: COLORS.textMuted }}>{l}</div>
                      <div style={{ fontSize: 11, fontWeight: 700 }}>{bwp(v)}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, padding: "6px 8px", background: COLORS.bg, borderRadius: 8 }}>
                  <span style={{ fontSize: 11, color: COLORS.textMuted }}>Expense Share</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.red }}>{bwp(m.share)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {state.archive.map((a, i) => (
        <div key={i} onClick={() => { setDetail(a); setSubTab("summary"); }} style={{ ...S.card, cursor: "pointer", display: "flex", gap: 12, alignItems: "flex-start", margin: "0 0 10px" }}>
          <div style={{ background: a.pool >= 0 ? `${COLORS.blue}18` : `${COLORS.red}18`, borderRadius: 10, padding: 8 }}>
            <Icon name="archive" size={20} color={a.pool >= 0 ? COLORS.blue : COLORS.red} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700 }}>{a.label}</div>
            <div style={{ fontSize: 12, color: COLORS.textMuted }}>Closed {a.closedAt} · {a.action === "roll" ? "Rolled Over" : "Distributed"}</div>
            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              {[["OB", a.ob], ["Contrib", a.tc], ["Exp", a.ta]].map(([l, v]) => (
                <div key={l} style={{ fontSize: 11, color: COLORS.textMuted }}>{l}: <strong>{bwp(v)}</strong></div>
              ))}
            </div>
          </div>
          <span style={{ fontWeight: 800, color: a.pool >= 0 ? COLORS.green : COLORS.red, fontSize: 15 }}>{bwp(a.pool)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── NAV ──────────────────────────────────────────────────────────────────────
const TABS = [
  { id: "home", label: "Home", icon: "home" },
  { id: "expenses", label: "Expenses", icon: "receipt" },
  { id: "contrib", label: "Contrib", icon: "wallet" },
  { id: "settle", label: "Settle", icon: "balance" },
  { id: "reports", label: "Reports", icon: "chart" },
  { id: "history", label: "History", icon: "archive" },
  { id: "members", label: "Members", icon: "people" },
  { id: "settings", label: "Settings", icon: "gear" },
];

// ─── APP ROOT ─────────────────────────────────────────────────────────────────
export default function App() {
  const [state, dispatch] = useReducer(reducer, null, loadState);
  const [tab, setTab] = useState("home");
  const [syncing, setSyncing] = useState(false);
  const [offline, setOffline] = useState(!navigator.onLine);
  const comp = compute(state);

  // Offline detection + auto-reload on reconnect
  useEffect(() => {
    function handleOffline() { setOffline(true); }
    function handleOnline() { setOffline(false); window.location.reload(); }
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    const poll = setInterval(() => {
      if (!navigator.onLine) setOffline(true);
      else if (offline) { setOffline(false); window.location.reload(); }
    }, 3000);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
      clearInterval(poll);
    };
  }, [offline]);

  // On mount: always try to load from Supabase
  useEffect(() => {
    setSyncing(true);
    supabaseLoad().then(remoteState => {
      if (remoteState && remoteState.setup) {
        dispatch({ type: "RESTORE_BACKUP", data: remoteState });
      }
      setSyncing(false);
    });
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.width = "100%";
    document.body.style.height = "100%";
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.height = "100%";
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
      document.documentElement.style.overflow = "";
      document.documentElement.style.height = "";
    };
  }, []);

  // ── Early returns (all hooks above this line) ──────────────────────────────

  // Show offline screen inline
  if (offline) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#f5f6fa", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: 40, fontFamily: "'DM Sans', sans-serif" }}>
        <style>{`@keyframes htpulse { 0%,100%{opacity:1} 50%{opacity:0.2} }`}</style>
        <svg width="80" height="80" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <polyline points="15,46 50,14 85,46" fill="none" stroke="#61dafb" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round"/>
          <line x1="22" y1="57" x2="78" y2="57" stroke="#1a2035" strokeWidth="6" strokeLinecap="round"/>
          <line x1="22" y1="70" x2="62" y2="70" stroke="#1a2035" strokeWidth="6" strokeLinecap="round" opacity="0.5"/>
          <line x1="22" y1="83" x2="68" y2="83" stroke="#1a2035" strokeWidth="6" strokeLinecap="round" opacity="0.25"/>
        </svg>
        <div style={{ fontSize: 26, fontWeight: 800, color: "#1a2035", letterSpacing: -0.5 }}>HouseTab</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", borderRadius: 99, padding: "7px 16px", boxShadow: "0 2px 10px rgba(0,0,0,0.08)" }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#ff5252", animation: "htpulse 1.4s infinite" }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: "#888" }}>You're offline</span>
        </div>
        <p style={{ fontSize: 14, color: "#aaa", lineHeight: 1.7, textAlign: "center", maxWidth: 250 }}>Will reload automatically when you're back online.</p>
      </div>
    );
  }

  if (syncing) {
    return (
      <div style={{ fontFamily: "'DM Sans', sans-serif", display: "flex", alignItems: "center", justifyContent: "center", position: "fixed", inset: 0, flexDirection: "column", gap: 16, background: COLORS.bg }}>
        <div style={{ width: 40, height: 40, border: `3px solid ${COLORS.blue}`, borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <div style={{ fontSize: 14, color: COLORS.textMuted, fontWeight: 500 }}>Restoring your data...</div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!state.setup) {
    return <Onboarding dispatch={dispatch} />;
  }

  const screens = {
    home: <Dashboard state={state} dispatch={dispatch} comp={comp} />,
    members: <Members state={state} dispatch={dispatch} comp={comp} />,
    contrib: <Contributions state={state} dispatch={dispatch} comp={comp} />,
    expenses: <Expenses state={state} dispatch={dispatch} comp={comp} />,
    settle: <Settle state={state} dispatch={dispatch} comp={comp} />,
    reports: <Reports state={state} comp={comp} dispatch={dispatch} />,
    history: <History state={state} />,
    settings: <Settings state={state} dispatch={dispatch} />,
  };

  const NAV_H = 56;
  const HEADER_H = 48;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: COLORS.navy, position: "fixed", inset: 0, display: "flex", justifyContent: "center" }}>
      <div style={{ width: "100%", maxWidth: 480, background: COLORS.bg, display: "flex", flexDirection: "column", height: "100%", position: "relative" }}>

        {/* Header */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: HEADER_H, background: COLORS.navy, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 18px", zIndex: 100 }}>
          <div style={{ color: COLORS.cyan, fontWeight: 800, fontSize: 20, letterSpacing: -0.5 }}>HouseTab</div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ color: "#aaa", fontSize: 13 }}>{state.period?.label}</div>
            <button onClick={() => window.location.reload(true)} title="Reload app" style={{ background: "none", border: "none", cursor: "pointer", color: "#666", padding: 4, display: "flex", alignItems: "center" }}>
              <Icon name="repeat" size={18} color="#666" />
            </button>
          </div>
        </div>

        {/* Offline banner */}
        {offline && (
          <div style={{ position: "absolute", top: HEADER_H, left: 0, right: 0, zIndex: 200, background: "#f59e0b", padding: "7px 16px", display: "flex", alignItems: "center", gap: 8 }}>
            <Icon name="warning" size={14} color="#fff" />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#fff" }}>You're offline — changes will sync when reconnected</span>
          </div>
        )}

        {/* Scrollable content */}
        <div style={{ position: "absolute", top: offline ? HEADER_H + 34 : HEADER_H, bottom: NAV_H, left: 0, right: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: "16px 14px 8px" }}>
          {screens[tab]}
        </div>

        {/* Bottom nav */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: NAV_H, background: "#fff", borderTop: `1px solid ${COLORS.border}`, display: "flex", zIndex: 100, boxShadow: "0 -2px 12px rgba(0,0,0,0.08)" }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "6px 2px 4px", background: "none", border: "none", cursor: "pointer", color: tab === t.id ? COLORS.blue : "#bbb", transition: "color 0.15s", fontFamily: "inherit" }}>
              <Icon name={t.icon} size={20} color={tab === t.id ? COLORS.blue : "#bbb"} />
              <span style={{ fontSize: 9, fontWeight: tab === t.id ? 700 : 500, marginTop: 2, letterSpacing: "0.2px" }}>{t.label}</span>
              {tab === t.id && <div style={{ width: 18, height: 3, background: COLORS.blue, borderRadius: 2, marginTop: 2 }} />}
            </button>
          ))}
        </div>

      </div>
    </div>
  );
}

